import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowDownRight, ArrowRight, ArrowUpRight, Check, Download, Info, LayoutGrid, Loader2, MessageSquare, RefreshCw, Table2, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getLaborSapReconnectErrorCode,
  isLaborSapReconnectError,
  LaborReconciliationDownloadError,
  laborPerformanceApi,
  type LaborP3RefreshJobResponse,
  type LaborP3RefreshResponse,
  type LaborPlantOption,
} from '../services/laborPerformanceService';
import { sapOAuthService } from '../services/sapOAuthService';
import { useGlobalToast } from '../components/UI/toaster';

type LaborRecord = Record<string, unknown>;

function readableErrorValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => readableErrorValue(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join('; ') : null;
  }
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    const directMessage = (
      readableErrorValue(record.detail) ||
      readableErrorValue(record.message) ||
      readableErrorValue(record.error) ||
      readableErrorValue(record.msg)
    );
    if (directMessage) return directMessage;

    const errors = readableErrorValue(record.errors);
    if (errors) return errors;

    try {
      return JSON.stringify(record);
    } catch {
      return null;
    }
  }
  return null;
}

function formatLaborPeriodLabel(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return 'Period ?';
  const fiscalMatch = text.match(/\b(?:(\d{3})\/\d{4}|\d{4}(\d{3}))\b/);
  if (fiscalMatch) {
    if (fiscalMatch[1]) return `Period ${fiscalMatch[1]}/${text.match(/\b\d{3}\/(\d{4})\b/)?.[1] ?? ''}`.trim();
    const year = text.match(/\b(\d{4})\d{3}\b/)?.[1];
    const period = fiscalMatch[2];
    return year ? `Period ${period}/${year}` : `Period ${period}`;
  }
  const periodMatch = text.match(/\bP(?:er(?:iod)?)?\s*(\d{1,2})\b/i);
  if (periodMatch) return `Period ${Number(periodMatch[1])}`;
  return text.toUpperCase();
}

function normalizeFiscalPeriod(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return '';
  const fiscalMatch = text.match(/\b(?:(\d{1,3})\/(\d{4})|(\d{4})(\d{3}))\b/);
  if (!fiscalMatch) return text;
  const period = Number(fiscalMatch[1] ?? fiscalMatch[4]);
  const year = fiscalMatch[2] ?? fiscalMatch[3];
  return Number.isFinite(period) && year ? `${String(period).padStart(3, '0')}/${year}` : text;
}

function getErrorMessage(err: unknown, periodLabel = 'P?'): string {
  if (typeof err === 'object' && err !== null) {
    const maybe = err as { response?: { data?: unknown; status?: number; statusText?: string }; message?: string };
    const responseMessage = readableErrorValue(maybe.response?.data);
    if (responseMessage) {
      if (
        /No successful DB ingestion source found for LABOR P3 calculation/i.test(responseMessage) ||
        /No labor reference rows found for source_file=/i.test(responseMessage)
      ) {
        return `The Labor ${periodLabel} report has not been generated yet. Please use Refresh and try again.`;
      }
      if (/<(?:!doctype\s+html|html|head|body|title|table|script)\b/i.test(responseMessage)) {
        return `Unable to load Labor ${periodLabel} report because an upstream SAP/network service returned a temporary error. Please try again later.`;
      }
      if (/\b(Cisco Secure Access|Umbrella|503 Service Unavailable|upstream server error|swg-proxy)\b/i.test(responseMessage)) {
        return `Unable to load Labor ${periodLabel} report because an upstream SAP/network service returned a temporary error. Please try again later.`;
      }
      return responseMessage;
    }
    if (maybe.message && !/\b(Internal Server Error|Request failed|status\s*5\d\d|500)\b/i.test(maybe.message)) {
      return maybe.message;
    }
  }
  return `Unable to load Labor ${periodLabel} report.`;
}

function getPlantLabel(plant: LaborPlantOption): string {
  return plant.plant_name ? `${plant.plant} - ${plant.plant_name}` : plant.plant;
}

function isUnavailableValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function formatCurrency(value: unknown): string {
  if (isUnavailableValue(value)) return 'N/A';
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num)) return 'N/A';
  if (num === 0) return '$0';
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function formatNumber(value: unknown): string {
  if (isUnavailableValue(value)) return 'N/A';
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num)) return 'N/A';
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatDecimal(value: unknown): string {
  if (isUnavailableValue(value)) return 'N/A';
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num)) return 'N/A';
  return num.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatRate(value: unknown): string {
  if (isUnavailableValue(value)) return 'N/A';
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num)) return 'N/A';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

function formatPercent(value: unknown): string {
  if (isUnavailableValue(value)) return 'N/A';
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num)) return 'N/A';
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function formatCompactCurrency(value: unknown): string {
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num) || num === 0) return '$0';
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1,
  }).format(num);
}

function formatCompactNumber(value: unknown): string {
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (!Number.isFinite(num) || num === 0) return '0';
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
}

type VarianceStatus = 'favorable' | 'unfavorable' | 'flat';

interface VarianceMetadata {
  amount?: unknown;
  basis?: unknown;
  pct?: unknown;
  status?: unknown;
}

function varianceMetadataFrom(value: unknown): VarianceMetadata | null {
  if (!isRecord(value)) return null;
  return value as VarianceMetadata;
}

function varianceMetadataFor(row: LaborRecord | undefined | null, field: string): VarianceMetadata | null {
  const metadata = recordFrom(row?.variance_metadata);
  return varianceMetadataFrom(metadata[field]);
}

function varianceStatus(value: unknown, metadata?: VarianceMetadata | null): VarianceStatus {
  if (isUnavailableValue(value) && isUnavailableValue(metadata?.amount)) return 'flat';
  const status = typeof metadata?.status === 'string' ? metadata.status.toLowerCase() : '';
  if (status === 'favorable' || status === 'unfavorable' || status === 'flat') return status;
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  if (num > 0) return 'favorable';
  if (num < 0) return 'unfavorable';
  return 'flat';
}

function varianceClass(value: unknown, metadata?: VarianceMetadata | null): string {
  const status = varianceStatus(value, metadata);
  if (status === 'favorable') return 'bg-[#087A22] text-white';
  if (status === 'unfavorable') return 'bg-[#E11919] text-white';
  return 'bg-white text-[#4D4649]';
}

function varianceTextClass(value: unknown, metadata?: VarianceMetadata | null): string {
  const status = varianceStatus(value, metadata);
  if (status === 'favorable') return 'text-[#087A22]';
  if (status === 'unfavorable') return 'text-[#B42318]';
  return 'text-[#6B5E62]';
}

function varianceSoftClass(value: unknown, metadata?: VarianceMetadata | null): string {
  const status = varianceStatus(value, metadata);
  if (status === 'favorable') return 'border-[#B8DEC1] bg-[#EDF7EF] text-[#087A22]';
  if (status === 'unfavorable') return 'border-[#F1B8B8] bg-[#FFF1F1] text-[#B42318]';
  return 'border-[#D8D0C9] bg-[#FAF7F1] text-[#6B5E62]';
}

function varianceStatusLabel(value: unknown, metadata?: VarianceMetadata | null): string {
  if (isUnavailableValue(value) && isUnavailableValue(metadata?.amount)) return 'N/A';
  const status = varianceStatus(value, metadata);
  if (status === 'favorable') return 'Favorable';
  if (status === 'unfavorable') return 'Unfavorable';
  return 'Flat';
}

function variancePctLabel(
  metadata?: VarianceMetadata | null,
  value?: unknown,
  basis?: unknown,
): string | null {
  const metadataPct = metadata?.pct;
  if (metadataPct !== undefined && metadataPct !== null && metadataPct !== '') {
    const pct = typeof metadataPct === 'number' ? metadataPct : Number(metadataPct);
    if (Number.isFinite(pct)) return formatPercent(pct);
  }

  if (isUnavailableValue(value) || isUnavailableValue(basis)) return null;
  const valueNum = toNumber(value);
  const basisNum = toNumber(basis);
  if (basisNum === 0) return null;
  return formatPercent((valueNum / basisNum) * 100);
}

function VarianceIcon({ value, metadata, className = 'h-3.5 w-3.5' }: { value: unknown; metadata?: VarianceMetadata | null; className?: string }): React.ReactElement {
  const status = varianceStatus(value, metadata);
  if (status === 'favorable') return <ArrowUpRight className={className} />;
  if (status === 'unfavorable') return <ArrowDownRight className={className} />;
  return <ArrowRight className={className} />;
}

function VariancePill({
  value,
  metadata,
  basis,
}: {
  value: unknown;
  metadata?: VarianceMetadata | null;
  basis?: unknown;
}): React.ReactElement {
  const pct = variancePctLabel(metadata, value, basis);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${varianceSoftClass(value, metadata)}`}>
      <VarianceIcon value={value} metadata={metadata} />
      <span>{varianceStatusLabel(value, metadata)}</span>
      {pct ? <span>{pct}</span> : null}
    </span>
  );
}

function VarianceValue({
  value,
  metadata,
  format = 'currency',
  compact = false,
  showPercent = true,
}: {
  value: unknown;
  metadata?: VarianceMetadata | null;
  format?: MetricColumn['format'];
  compact?: boolean;
  showPercent?: boolean;
}): React.ReactElement {
  const pct = variancePctLabel(metadata);
  const formattedValue = format === 'rate'
    ? formatRate(value)
    : format === 'decimal'
      ? formatDecimal(value)
      : format === 'percent'
        ? formatPercent(value)
        : format === 'number'
          ? formatNumber(value)
          : formatCurrency(value);
  return (
    <span className="inline-flex flex-col items-end gap-0.5 leading-tight">
      <span>{formattedValue}</span>
      <span className={`inline-flex items-center justify-end gap-1 text-[10px] font-semibold ${compact ? 'text-white' : varianceTextClass(value, metadata)}`}>
        <VarianceIcon value={value} metadata={metadata} className="h-3 w-3" />
        <span>{varianceStatusLabel(value, metadata)}</span>
        {showPercent && pct ? <span>{pct}</span> : null}
      </span>
    </span>
  );
}

function varianceAmountHeader(column: MetricColumn): string {
  if (column.format === 'currency') return 'Variance (in $)';
  if (column.format === 'decimal') return 'Variance';
  if (column.format === 'rate') return column.label.replace('$ Var', 'Variance');
  return column.label;
}

function toNumber(value: unknown): number {
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
}

function isRecord(value: unknown): value is LaborRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function recordFrom(value: unknown): LaborRecord {
  return isRecord(value) ? value : {};
}

function rowsFrom(value: unknown): LaborRecord[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function rowDateInRange(row: { isoDate?: string; work_date?: unknown }, startDate?: string | null, endDate?: string | null): boolean {
  const isoDate = row.isoDate ?? stringValue(row.work_date);
  if (!isoDate) return false;
  if (startDate && isoDate < startDate) return false;
  return !(endDate && isoDate > endDate);
}

function sumBy(rows: LaborRecord[], key: string): number {
  return rows.reduce((total, row) => total + toNumber(row[key]), 0);
}

function sumByWhen(rows: LaborRecord[], key: string, predicate: (row: LaborRecord) => boolean): number {
  return rows.reduce((total, row) => (predicate(row) ? total + toNumber(row[key]) : total), 0);
}

function formatDateLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
}

function formatFullDateLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTimeLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

interface DataQualityMessage {
  id: string;
  title: string;
  body: string;
}

function buildLaborDataQualityMessages(reportMetadata: LaborRecord, reportPeriodLabel: string): DataQualityMessage[] {
  const dataQuality = recordFrom(reportMetadata.data_quality);
  const messages: DataQualityMessage[] = [];

  const missingTempRates = recordFrom(dataQuality.temp_labor_missing_rates);
  const missingTempRateRows = toNumber(missingTempRates.row_count);
  if (missingTempRateRows > 0) {
    const hours = toNumber(missingTempRates.hours);
    const costCenters = toNumber(missingTempRates.cost_center_count);
    const dateFrom = formatFullDateLabel(missingTempRates.date_from);
    const dateTo = formatFullDateLabel(missingTempRates.date_to);
    const dateRange = dateFrom && dateTo ? ` between ${dateFrom} and ${dateTo}` : '';
    const hoursText = hours > 0 ? ` covering ${formatDecimal(hours)} hours` : '';
    const costCenterText = costCenters > 0 ? ` across ${formatNumber(costCenters)} cost centers` : '';
    messages.push({
      id: 'temp-labor-missing-rates',
      title: 'Temp labor rates need review',
      body: `${formatNumber(missingTempRateRows)} contingent labor rows${dateRange} have missing or zero hourly rates${hoursText}${costCenterText}. Temp labor spend may be understated until those rates are corrected in Workday.`,
    });
  }

  const staticInputs = recordFrom(dataQuality.static_inputs);
  if (staticInputs.fallback_used === true) {
    const requestedPeriod = stringValue(staticInputs.requested_period) ?? reportPeriodLabel;
    const sourcePeriod = stringValue(staticInputs.source_period) ?? 'the closest available period';
    const requestedPeriodLabel = formatLaborPeriodLabel(requestedPeriod);
    const sourcePeriodLabel = sourcePeriod === 'the closest available period'
      ? sourcePeriod
      : formatLaborPeriodLabel(sourcePeriod);
    messages.push({
      id: 'static-inputs-fallback',
      title: 'Manual adjustments are using a fallback period',
      body: `${requestedPeriodLabel} business workbook is not available, so SmartCHG used ${sourcePeriodLabel} workbook for manual adjustments. Credits, debits, allocation %, tax assumptions, active-day flags, base quantity, headcount, and mixing rates may differ from the requested period.`,
    });
  }

  return messages;
}

function getCell(report: LaborRecord | null, group: string, cell: string): unknown {
  const laborP3Report = recordFrom(report?.labor_p3_report);
  const groupValues = recordFrom(laborP3Report[group]);
  return groupValues[cell] ?? 0;
}

interface LaborKpiCardData {
  id: string;
  label: string;
  value: number;
  format: 'currency' | 'number' | 'decimal';
  helper: string;
  variance?: boolean;
  varianceBasis?: number;
  varianceMeta?: VarianceMetadata | null;
}

interface MetricColumn {
  key: string;
  label: string;
  format: 'currency' | 'number' | 'decimal' | 'rate' | 'percent';
  variance?: boolean;
  summary?: 'sum' | 'blank';
}

interface LaborDetailGridProps {
  title: string;
  section: 'fixed' | 'variable';
  contentWidth: number;
  data?: LaborDetailData | null;
}

interface LaborDetailData {
  columns?: string[];
  rows?: LaborRecord[];
  footer?: { totals?: LaborRecord; by_label?: Record<string, LaborRecord> };
  scheduled_days?: number | null;
}

interface DailyLaborChartRow {
  date: string;
  isoDate: string;
  fixedSpend: number | null;
  fixedBudget: number;
  fixedVariance: number | null;
  fixedVarianceMeta?: VarianceMetadata | null;
  variableSpend: number | null;
  variableEarned: number | null;
  variableVariance: number | null;
  variableVarianceMeta?: VarianceMetadata | null;
  netVarianceMeta?: VarianceMetadata | null;
  producedUnits: number | null;
}

interface LineLaborChartRow {
  lineName: string;
  spend: number | null;
  earned: number | null;
  variance: number | null;
  varianceMeta?: VarianceMetadata | null;
  producedUnits: number | null;
  unitOfMeasure: string;
  earnedHours: number | null;
}

interface FixedDepartmentSummaryRow {
  label: string;
  spend: number;
  budget: number;
  variance: number;
  varianceMeta?: VarianceMetadata | null;
  budgetAvailable: boolean;
}

interface DailyExceptionRow {
  date: string;
  spend: number | null;
  plannedEarned: number | null;
  variance: number | null;
  varianceMeta?: VarianceMetadata | null;
  units: number | null;
}

type LaborReportView = 'dashboard' | 'grid';
type LaborGridSection = 'fixed' | 'variable' | 'all';

const DEFAULT_PLANT = '2800';
const DEFAULT_FISCAL_YEAR_PERIOD = '003/2027';
const REFRESH_FAILED_MESSAGE = "We couldn't refresh the report because SAP returned a temporary error while preparing production data. Please try again later.";
const SAP_RECONNECT_MESSAGE = 'SAP Datasphere needs to be reconnected before refreshing Labor Performance.';
const PENDING_LABOR_SAP_REFRESH_KEY = 'smartchg:labor-performance:pending-sap-refresh';
const LABOR_SELECTION_STORAGE_KEY = 'smartchg:labor-performance:selection';
const LABOR_PLANT_QUERY_PARAM = 'plant';
const LABOR_PERIOD_QUERY_PARAM = 'fiscal_year_period';

interface LaborSelectionState {
  plant?: string;
  fiscalPeriod?: string;
}

interface InitialLaborSelection extends LaborSelectionState {
  hasExplicitSelection: boolean;
}

function cleanSelectionValue(value: unknown): string | undefined {
  const text = String(value ?? '').trim();
  return text || undefined;
}

function readStoredLaborSelection(): LaborSelectionState {
  try {
    const raw = window.localStorage.getItem(LABOR_SELECTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LaborSelectionState;
    return {
      plant: cleanSelectionValue(parsed.plant),
      fiscalPeriod: cleanSelectionValue(parsed.fiscalPeriod),
    };
  } catch {
    return {};
  }
}

function readInitialLaborSelection(searchParams: URLSearchParams): InitialLaborSelection {
  const stored = readStoredLaborSelection();
  const queryPlant = cleanSelectionValue(searchParams.get(LABOR_PLANT_QUERY_PARAM));
  const queryPeriod = cleanSelectionValue(
    searchParams.get(LABOR_PERIOD_QUERY_PARAM) ?? searchParams.get('period'),
  );
  return {
    plant: queryPlant ?? stored.plant,
    fiscalPeriod: queryPeriod ?? stored.fiscalPeriod,
    hasExplicitSelection: Boolean(queryPlant || queryPeriod || stored.plant || stored.fiscalPeriod),
  };
}

function persistLaborSelection(plant: string, fiscalPeriod: string): void {
  try {
    window.localStorage.setItem(
      LABOR_SELECTION_STORAGE_KEY,
      JSON.stringify({ plant, fiscalPeriod }),
    );
  } catch {
    // No-op; URL params still preserve the current view when storage is blocked.
  }
}

function normalizePlantOptions(rows: LaborPlantOption[]): LaborPlantOption[] {
  const seen = new Set<string>();
  const normalized = rows
    .map((row) => {
      const plant = String(row.plant ?? '').trim();
      const plantName = typeof row.plant_name === 'string' ? row.plant_name.trim() : '';
      return {
        plant,
        plant_name: plantName || plant,
        source: row.source,
      };
    })
    .filter((row) => row.plant.length === 4)
    .filter((row) => row.plant_name && row.plant_name !== row.plant)
    .filter((row) => {
      if (seen.has(row.plant)) return false;
      seen.add(row.plant);
      return true;
    });

  return normalized.length > 0
    ? normalized
    : [{ plant: DEFAULT_PLANT, plant_name: 'Carlstadt', source: 'default' }];
}

interface SectionOption<T extends string> {
  id: T;
  label: string;
}

interface CsvSection {
  title: string;
  headers: string[];
  rows: unknown[][];
}

const fixedDepartmentMap = [
  {
    setupKey: 'maintenance',
    label: 'Maintenance',
    emp: 'maintenance_employee',
    temps: 'maintenance_temps',
    budget: 'maintenance_budget',
    variance: 'maintenance_variance',
  },
  {
    setupKey: 'sanitation',
    label: 'Sanitation',
    emp: 'sanitation_employee',
    temps: 'sanitation_temps',
    budget: 'sanitation_budget',
    variance: 'sanitation_variance',
  },
  {
    setupKey: 'quality',
    label: 'Quality',
    emp: 'quality_employee',
    temps: 'quality_temps',
    budget: 'quality_budget',
    variance: 'quality_variance',
  },
  {
    setupKey: 'warehouse',
    label: 'Warehouse',
    emp: 'warehouse_employee',
    temps: 'warehouse_temps',
    budget: 'warehouse_budget',
    variance: 'warehouse_variance',
  },
  {
    setupKey: 'general_mfg',
    label: 'General Mfg / Safety',
    emp: 'safety_spent',
    temps: null,
    budget: 'general_mfg_budget',
    variance: 'safety_variance',
  },
] as const;

const gridSectionOptions: SectionOption<LaborGridSection>[] = [
  { id: 'fixed', label: 'Fixed Labor' },
  { id: 'variable', label: 'Variable Labor' },
  { id: 'all', label: 'All' },
];

const feedbackCategoryOptions = ['Data issue', 'UI issue', 'Question', 'Other'];
const feedbackSentimentOptions = [
  { id: 'good', label: 'Good', icon: ThumbsUp },
  { id: 'bad', label: 'Bad', icon: ThumbsDown },
] as const;

const fixedTotalColumns: MetricColumn[] = [
  { key: 'spent', label: 'Spent', format: 'currency' },
  { key: 'budget', label: 'Budget', format: 'currency' },
  { key: 'variance', label: '$ Var', format: 'currency', variance: true },
];

const fixedItemColumns: MetricColumn[] = [
  { key: 'emp', label: 'EMP', format: 'currency' },
  { key: 'temps', label: 'Temps', format: 'currency' },
  { key: 'budget', label: 'Budget', format: 'currency' },
  { key: 'variance', label: '$ Var', format: 'currency', variance: true },
];

const variableTotalColumns: MetricColumn[] = [
  { key: 'spent', label: 'Spent', format: 'currency' },
  { key: 'earned', label: 'Earned', format: 'currency' },
  { key: 'variance', label: '$ Var', format: 'currency', variance: true },
  { key: 'units', label: 'Total Units', format: 'number' },
  { key: 'spent_per_unit', label: 'Spent Per Cs', format: 'rate' },
  { key: 'earned_per_unit', label: 'Earned Per Cs', format: 'rate' },
  { key: 'variance_per_unit', label: '$ Var Per Cs', format: 'rate', variance: true },
  { key: 'spent_hours', label: 'Spent Hours', format: 'decimal' },
  { key: 'earned_hours', label: 'Earned Hours', format: 'decimal' },
  { key: 'hours_var', label: 'Hours Var', format: 'decimal', variance: true },
  { key: 'hc', label: 'HC', format: 'decimal' },
];

const variableSummaryTotalColumns: MetricColumn[] = [
  { key: 'total_variable_spent', label: 'Spent', format: 'currency' },
  { key: 'total_variable_earned', label: 'Earned', format: 'currency' },
  { key: 'total_variable_variance', label: '$ Var', format: 'currency', variance: true },
  { key: 'units', label: 'Total Units', format: 'number' },
  { key: 'spent_per_case', label: 'Spent Per Cs', format: 'rate' },
  { key: 'earned_per_case', label: 'Earned Per Cs', format: 'rate' },
  { key: 'variance_per_case', label: '$ Var Per Cs', format: 'rate', variance: true },
  { key: 'actual_hours', label: 'Spent Hours', format: 'decimal' },
  { key: 'earned_hours', label: 'Earned Hours', format: 'decimal' },
  { key: 'hours_variance', label: 'Hours Var', format: 'decimal', variance: true },
  { key: 'total_headcount', label: 'HC', format: 'decimal' },
];

const variableItemColumns: MetricColumn[] = [
  { key: 'emp', label: 'EMP', format: 'currency' },
  { key: 'benefits', label: 'Benefits', format: 'currency' },
  { key: 'temps', label: 'Temps', format: 'currency' },
  { key: 'earned', label: 'Earned', format: 'currency' },
  { key: 'variance', label: '$ Var', format: 'currency', variance: true },
  { key: 'units', label: 'Produced', format: 'number' },
  { key: 'spent_per_unit', label: 'Spent Per Cs', format: 'rate' },
  { key: 'earned_per_unit', label: 'Earned Per Cs', format: 'rate' },
  { key: 'variance_per_unit', label: '$ Var Per Cs', format: 'rate', variance: true },
  { key: 'emp_hours', label: 'Emp Hours', format: 'decimal' },
  { key: 'temp_hours', label: 'Temp Hours', format: 'decimal' },
  { key: 'earned_hours', label: 'Earned Hours', format: 'decimal' },
  { key: 'hours_var', label: 'Hours Var', format: 'decimal', variance: true },
  { key: 'hc', label: 'HC', format: 'decimal' },
];

function variableLineItemColumns(lineName: string): MetricColumn[] {
  const normalized = lineName.trim().toLowerCase();
  const isMixing = normalized === 'mixing';
  const isCaseLine = normalized.includes('heuft') || normalized.includes('flow wrap');
  const isRack = normalized === 'rack';
  const producedLabel = isMixing
    ? 'Pounds Produced'
    : isCaseLine
      ? 'Cases Produced'
      : isRack
        ? 'Units Produced'
        : 'Produced';
  const rateUnit = isMixing ? 'LB' : 'Cs';

  return variableItemColumns.map((column) => {
    if (column.key === 'units') return { ...column, label: producedLabel };
    if (column.key === 'spent_per_unit') return { ...column, label: `Spent Per ${rateUnit}` };
    if (column.key === 'earned_per_unit') return { ...column, label: `Earned Per ${rateUnit}` };
    if (column.key === 'variance_per_unit') return { ...column, label: `$ Var Per ${rateUnit}` };
    return column;
  });
}

const variableSummaryGroups = [
  {
    label: 'Total Variable Labor',
    columns: variableSummaryTotalColumns,
  },
  {
    label: 'Packaging',
    columns: [
      { key: 'packaging_employee_spend', label: 'EMP', format: 'currency' },
      { key: 'packaging_benefits', label: 'Benefits', format: 'currency' },
      { key: 'packaging_temp_spend', label: 'Temps', format: 'currency' },
      { key: 'packaging_earned', label: 'Earned', format: 'currency' },
      { key: 'packaging_variance', label: '$ Var', format: 'currency', variance: true },
      { key: 'packaging_units_produced', label: 'Cases Produced', format: 'number' },
      { key: 'packaging_employee_hours', label: 'Emp Hours', format: 'decimal' },
      { key: 'packaging_temp_hours', label: 'Temp Hours', format: 'decimal' },
      { key: 'packaging_headcount', label: 'HC', format: 'decimal' },
    ],
  },
  {
    label: 'Mixing',
    columns: [
      { key: 'mixing_employee_spend', label: 'EMP', format: 'currency' },
      { key: 'mixing_benefits', label: 'Benefits', format: 'currency' },
      { key: 'mixing_temp_spend', label: 'Temps', format: 'currency' },
      { key: 'mixing_earned', label: 'Earned', format: 'currency' },
      { key: 'mixing_variance', label: '$ Var', format: 'currency', variance: true },
      { key: 'mixing_units_produced', label: 'Pounds Produced', format: 'number' },
      { key: 'mixing_employee_hours', label: 'Emp Hours', format: 'decimal' },
      { key: 'mixing_temp_hours', label: 'Temp Hours', format: 'decimal' },
      { key: 'mixing_headcount', label: 'HC', format: 'decimal' },
    ],
  },
] satisfies Array<{ label: string; columns: MetricColumn[] }>;

const fixedSupportColumns: MetricColumn[] = [
  { key: 'don_doff_debit', label: 'DON/DOFF Debit', format: 'currency' },
  { key: 'misc_debit', label: 'MISC Debit', format: 'currency' },
  { key: 'cases_shipped', label: 'Cases Shipped', format: 'number' },
  { key: 'open_orders', label: 'Open Orders', format: 'number' },
  { key: 'production_variance', label: 'Production Var', format: 'number' },
];

const variableAdjustmentColumns: MetricColumn[] = [
  { key: 'training_credit', label: 'Training Credits', format: 'currency' },
  { key: 'packaging_labor_pivots_debit', label: 'Donning & Doffing Debits', format: 'currency' },
  { key: 'variable_vacation_debit', label: 'Variable Vacation Debits', format: 'currency' },
  { key: 'temp_labor_true_up_debit', label: 'Temp Labor True Up Debits', format: 'currency' },
  { key: 'packaging_true_up', label: 'True Up', format: 'currency' },
];

function formatMetric(value: unknown, column: MetricColumn): string {
  if (column.format === 'currency') return formatCurrency(value);
  if (column.format === 'rate') return formatRate(value);
  if (column.format === 'decimal') return formatDecimal(value);
  if (column.format === 'percent') return formatPercent(value);
  return formatNumber(value);
}

function lineTotalValue(valuesByCell: LaborRecord, fieldByCell: LaborRecord, fieldName: string): unknown {
  const cellEntry = Object.entries(fieldByCell).find(([, mappedField]) => mappedField === fieldName);
  return cellEntry ? valuesByCell[cellEntry[0]] : 0;
}

function buildFooter(rows: LaborRecord[], childKey: 'departments' | 'lines', labels: string[], metrics: string[]) {
  const totals = metrics.reduce<Record<string, number>>((acc, metric) => {
    acc[metric] = sumBy(rows, metric);
    return acc;
  }, {});
  const byLabel = labels.reduce<Record<string, Record<string, number>>>((acc, label) => {
    acc[label] = metrics.reduce<Record<string, number>>((metricTotals, metric) => {
      metricTotals[metric] = 0;
      return metricTotals;
    }, {});
    return acc;
  }, {});

  rows.forEach((row) => {
    const children = rowsFrom(row[childKey]);
    children.forEach((child) => {
      const label = String(child.label ?? '');
      if (!byLabel[label]) return;
      metrics.forEach((metric) => {
        byLabel[label][metric] += toNumber(child[metric]);
      });
    });
  });

  return { totals, by_label: byLabel };
}

function buildFixedDetailView(report: LaborRecord | null) {
  const rows = rowsFrom(recordFrom(report?.fixed_labor).rows);
  const controlCells = recordFrom(recordFrom(report?.labor_p3_report).control_cells);
  const viewRows = rows.map((row) => ({
    week: row.week_number ?? '',
    day: row.weekday ?? '',
    date: formatDateLabel(row.work_date),
    iso_date: row.work_date,
    active: row.active_flag,
    spent: toNumber(row.total_fixed_spent),
    budget: toNumber(row.total_fixed_budget),
    variance: toNumber(row.total_fixed_variance),
    variance_metadata: {
      variance: varianceMetadataFor(row, 'total_fixed_variance'),
    },
    departments: fixedDepartmentMap.map((department) => ({
      label: department.label,
      emp: toNumber(row[department.emp]),
      temps: department.temps ? toNumber(row[department.temps]) : 0,
      budget: toNumber(row[department.budget]),
      variance: toNumber(row[department.variance]),
      variance_metadata: {
        variance: varianceMetadataFor(row, department.variance),
      },
    })),
  }));
  const labels = fixedDepartmentMap.map((department) => department.label);
  return {
    title: 'Fixed Labor Daily Grid',
    columns: labels,
    scheduled_days: typeof controlCells.A82 === 'number' ? controlCells.A82 : null,
    footer: buildFooter(viewRows, 'departments', labels, ['emp', 'temps', 'budget', 'variance', 'spent']),
    rows: viewRows,
  };
}

function buildVariableDetailView(report: LaborRecord | null) {
  const rows = rowsFrom(recordFrom(report?.variable_labor).rows);
  const lineTables = recordFrom(report?.line_labor);
  const controlCells = recordFrom(recordFrom(report?.labor_p3_report).control_cells);
  const labels = Object.keys(lineTables);
  const viewRows = rows.map((row, rowIndex) => ({
    week: row.week_number ?? '',
    day: row.weekday ?? '',
    date: formatDateLabel(row.work_date),
    iso_date: row.work_date,
    active: row.active_flag,
    spent: row.total_variable_spent,
    earned: row.total_variable_earned,
    variance: row.total_variable_variance,
    variance_metadata: {
      variance: varianceMetadataFor(row, 'total_variable_variance'),
      variance_per_unit: varianceMetadataFor(row, 'variance_per_case'),
      hours_var: varianceMetadataFor(row, 'hours_variance'),
    },
    units: row.units,
    spent_per_unit: row.spent_per_case,
    earned_per_unit: row.earned_per_case,
    variance_per_unit: row.variance_per_case,
    spent_hours: row.actual_hours,
    earned_hours: row.earned_hours,
    hours_var: row.hours_variance,
    hc: row.total_headcount,
    lines: labels.map((label) => {
      const lineRows = rowsFrom(recordFrom(lineTables[label]).rows);
      const lineRow = lineRows[rowIndex] ?? {};
      return {
        label,
        emp: lineRow.employee_spend,
        benefits: toNumber(lineRow.benefits),
        temps: lineRow.temp_spend,
        earned: lineRow.earned,
        variance: lineRow.variance,
        variance_metadata: {
          variance: varianceMetadataFor(lineRow, 'variance'),
          variance_per_unit: varianceMetadataFor(lineRow, 'variance_per_case'),
          hours_var: varianceMetadataFor(lineRow, 'hours_variance'),
        },
        units: lineRow.units_produced,
        spent_per_unit: lineRow.spent_per_case,
        earned_per_unit: lineRow.earned_per_case,
        variance_per_unit: lineRow.variance_per_case,
        emp_hours: lineRow.employee_hours,
        temp_hours: lineRow.temp_hours,
        earned_hours: lineRow.earned_hours,
        hours_var: lineRow.hours_variance,
        hc: lineRow.headcount,
      };
    }),
  }));
  return {
    title: 'Variable Labor Daily Grid',
    columns: labels,
    scheduled_days: typeof controlCells.A82 === 'number' ? controlCells.A82 : null,
    footer: buildFooter(viewRows, 'lines', labels, [
      'emp',
      'benefits',
      'temps',
      'earned',
      'variance',
      'units',
      'emp_hours',
      'temp_hours',
      'earned_hours',
      'hours_var',
      'hc',
    ]),
    rows: viewRows,
  };
}

function buildDetailSectionOptions(data?: LaborDetailData | null): SectionOption<string>[] {
  const columns = Array.isArray(data?.columns) ? data.columns : [];
  return [
    { id: 'all', label: 'All' },
    ...columns.map((column) => ({ id: column, label: column })),
  ];
}

function filterDetailView(data: LaborDetailData | null | undefined, childKey: 'departments' | 'lines', selectedSections: string[]): LaborDetailData | null {
  if (!data) return null;
  const columns = Array.isArray(data.columns) ? data.columns : [];
  if (selectedSections.includes('all')) return data;

  const selectedColumns = columns.filter((column) => selectedSections.includes(column));
  if (selectedColumns.length === 0) return data;

  const selectedColumnSet = new Set(selectedColumns);
  const footerByLabel = selectedColumns.reduce<Record<string, LaborRecord>>((acc, column) => {
    const footer = data.footer?.by_label?.[column];
    if (footer) acc[column] = footer;
    return acc;
  }, {});

  return {
    ...data,
    columns: selectedColumns,
    rows: rowsFrom(data.rows).map((row) => ({
      ...row,
      [childKey]: rowsFrom(row[childKey]).filter((child) => selectedColumnSet.has(String(child.label ?? ''))),
    })),
    footer: data.footer
      ? {
          ...data.footer,
          by_label: footerByLabel,
        }
      : undefined,
  };
}

function metricCsvValue(value: unknown): unknown {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : String(value);
}

function metricCsvHeaders(column: MetricColumn): string[] {
  if (!column.variance) return [column.label];
  return [varianceAmountHeader(column), 'Variance (percentage)', 'Favourable/Unfavourable'];
}

function metricCsvValues(row: LaborRecord | undefined | null, column: MetricColumn): unknown[] {
  const value = row?.[column.key];
  if (!column.variance) return [metricCsvValue(value)];
  const metadata = metricVarianceMetadata(row, column);
  return [
    metricCsvValue(value),
    variancePctLabel(metadata) ?? '',
    varianceStatusLabel(value, metadata),
  ];
}

function metricFooterCsvValue(rows: LaborRecord[], column: MetricColumn): unknown {
  if (column.summary === 'blank' || column.format === 'rate') return '';
  return sumBy(rows, column.key);
}

function metricFooterCsvValues(rows: LaborRecord[], column: MetricColumn): unknown[] {
  const value = metricFooterCsvValue(rows, column);
  if (!column.variance) return [value];
  const basis = metricFooterBasis(rows, column);
  return [
    value,
    value === '' ? '' : variancePctLabel(null, value, basis) ?? '',
    value === '' ? '' : varianceStatusLabel(value),
  ];
}

function sanitizeFilenamePart(value: string): string {
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return normalized || 'report';
}

function isLikelyCurrencyHeader(header: string): boolean {
  const normalized = header.toLowerCase();
  return (
    normalized.includes('spent') ||
    normalized.includes('budget') ||
    normalized.includes('$ var') ||
    normalized.includes('(in $)') ||
    normalized.includes('earned') ||
    normalized.includes('benefit') ||
    normalized.includes('credit') ||
    normalized.includes('debit') ||
    normalized.includes('true up') ||
    normalized.includes('amt')
  );
}

function isLikelyPercentHeader(header: string): boolean {
  const normalized = header.trim().toLowerCase();
  return normalized === '%' || normalized.endsWith(' - %') || normalized.includes('percent');
}

function isLikelyVarianceHeader(header: string): boolean {
  const normalized = header.toLowerCase();
  return normalized.includes('$ var') || normalized.includes('variance') || normalized.includes('hours var');
}

function excelNumberFormatForHeader(header: string): string | undefined {
  const normalized = header.toLowerCase();
  if (isLikelyPercentHeader(header)) return '0.0"%"';
  if (isLikelyCurrencyHeader(header)) return '$#,##0;[Red]($#,##0);$0';
  if (normalized.includes('rate') || normalized.includes('per cs') || normalized.includes('per lb')) return '$0.00';
  if (normalized.includes('hours') || normalized === 'hc' || normalized.endsWith(' - hc')) return '#,##0.0';
  if (normalized.includes('produced') || normalized.includes('units') || normalized.includes('cases') || normalized.includes('pounds') || normalized.includes('orders')) return '#,##0';
  return undefined;
}

function sanitizeWorksheetName(title: string, fallback: string, usedNames: Set<string>): string {
  const baseName = title
    .replace(/[\][*?:/\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31) || fallback;
  let name = baseName;
  let suffix = 2;
  while (usedNames.has(name)) {
    const suffixText = ` ${suffix}`;
    name = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`;
    suffix += 1;
  }
  usedNames.add(name);
  return name;
}

async function downloadExcel(filename: string, sections: CsvSection[]): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'SmartCHG';
  workbook.created = new Date();
  workbook.modified = new Date();

  const usedSheetNames = new Set<string>();
  sections.forEach((section, sectionIndex) => {
    const worksheet = workbook.addWorksheet(
      sanitizeWorksheetName(section.title, `Grid ${sectionIndex + 1}`, usedSheetNames),
      { views: [{ state: 'frozen', ySplit: 2 }] },
    );
    const columnCount = Math.max(section.headers.length, 1);
    worksheet.mergeCells(1, 1, 1, columnCount);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = section.title;
    titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6E3450' } };
    titleCell.border = {
      top: { style: 'thin', color: { argb: 'FF1F1A20' } },
      bottom: { style: 'thin', color: { argb: 'FF1F1A20' } },
    };
    worksheet.getRow(1).height = 24;

    const headerRow = worksheet.getRow(2);
    section.headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true, color: { argb: 'FF1F1A20' }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: index < 3 ? 'left' : 'right', wrapText: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: index === 0 ? 'FFFFFF00' : 'FFD7D7D7' },
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFB9B0AA' } },
        left: { style: 'thin', color: { argb: 'FFB9B0AA' } },
        bottom: { style: 'thin', color: { argb: 'FFB9B0AA' } },
        right: { style: 'thin', color: { argb: 'FFB9B0AA' } },
      };
    });
    headerRow.height = 30;

    section.rows.forEach((rowValues, rowIndex) => {
      const excelRow = worksheet.getRow(rowIndex + 3);
      const isTotal = String(rowValues[2] ?? rowValues[0] ?? '').toLowerCase() === 'total';
      rowValues.forEach((value, columnIndex) => {
        const cell = excelRow.getCell(columnIndex + 1);
        cell.value = value as never;
        cell.alignment = { vertical: 'middle', horizontal: columnIndex < 3 ? 'left' : 'right' };
        const header = section.headers[columnIndex] ?? '';
        const numFmt = typeof value === 'number' ? excelNumberFormatForHeader(header) : undefined;
        if (numFmt) cell.numFmt = numFmt;

        const variance = isLikelyVarianceHeader(header) && typeof value === 'number';
        cell.fill = variance && value !== 0
          ? {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: value > 0 ? 'FF087A22' : 'FFE11919' },
            }
          : {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: columnIndex === 0 ? 'FFFFFF00' : isTotal ? 'FFFAF7F1' : 'FFFFFFFF' },
            };
        cell.font = {
          bold: isTotal || columnIndex === 0,
          color: { argb: variance && value !== 0 ? 'FFFFFFFF' : 'FF1F1A20' },
          size: 10,
        };
        cell.border = {
          top: { style: isTotal ? 'medium' : 'thin', color: { argb: isTotal ? 'FF1F1A20' : 'FFD8D0C9' } },
          left: { style: 'thin', color: { argb: 'FFD8D0C9' } },
          bottom: { style: 'thin', color: { argb: 'FFD8D0C9' } },
          right: { style: 'thin', color: { argb: 'FFD8D0C9' } },
        };
      });
    });

    worksheet.columns = section.headers.map((header, index) => {
      const longestValueLength = section.rows.reduce((maxLength, row) => {
        const value = row[index];
        return Math.max(maxLength, String(value ?? '').length);
      }, header.length);
      return {
        width: Math.min(Math.max(index < 3 ? 11 : 13, longestValueLength + 2), index < 3 ? 16 : 22),
      };
    });
    worksheet.autoFilter = {
      from: { row: 2, column: 1 },
      to: { row: 2, column: columnCount },
    };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildFlatDailyCsvSection(title: string, rows: LaborRecord[], columns: MetricColumn[]): CsvSection | null {
  if (rows.length === 0 || columns.length === 0) return null;
  return {
    title,
    headers: ['Week', 'Day', 'Date', 'Active', ...columns.flatMap(metricCsvHeaders)],
    rows: [
      ...rows.map((row) => [
        dailyWeekValue(row),
        dailyDayValue(row),
        dailyDateValue(row),
        dailyActiveValue(row),
        ...columns.flatMap((column) => metricCsvValues(row, column)),
      ]),
      [
        '',
        '',
        'Total',
        '',
        ...columns.flatMap((column) => metricFooterCsvValues(rows, column)),
      ],
    ],
  };
}

function buildGroupedDailyCsvSection(
  title: string,
  rows: LaborRecord[],
  groups: Array<{ label: string; columns: MetricColumn[] }>,
): CsvSection | null {
  const groupedColumns = groups.flatMap((group) => (
    group.columns.map((column) => ({ groupLabel: group.label, column }))
  ));
  if (rows.length === 0 || groupedColumns.length === 0) return null;
  return {
    title,
    headers: [
      'Week',
      'Day',
      'Date',
      'Active',
      ...groupedColumns.flatMap(({ groupLabel, column }) => (
        metricCsvHeaders(column).map((header) => `${groupLabel} - ${header}`)
      )),
    ],
    rows: [
      ...rows.map((row) => [
        dailyWeekValue(row),
        dailyDayValue(row),
        dailyDateValue(row),
        dailyActiveValue(row),
        ...groupedColumns.flatMap(({ column }) => metricCsvValues(row, column)),
      ]),
      [
        '',
        '',
        'Total',
        '',
        ...groupedColumns.flatMap(({ column }) => metricFooterCsvValues(rows, column)),
      ],
    ],
  };
}

function buildDetailGridCsvSection(
  title: string,
  section: 'fixed' | 'variable',
  data?: LaborDetailData | null,
  showTotals = true,
): CsvSection | null {
  const columns = Array.isArray(data?.columns) ? data.columns : [];
  const rows = rowsFrom(data?.rows);
  if (columns.length === 0 || rows.length === 0) return null;

  const childKey = section === 'fixed' ? 'departments' : 'lines';
  const totalColumns = showTotals ? (section === 'fixed' ? fixedTotalColumns : variableTotalColumns) : [];
  const itemColumnsForLabel = (label: string) => (
    section === 'fixed' ? fixedItemColumns : variableLineItemColumns(label)
  );
  const headers = [
    'Week',
    'Day',
    'Date',
    'Active',
    ...totalColumns.flatMap((column) => (
      metricCsvHeaders(column).map((header) => `Total ${section === 'fixed' ? 'Fixed Labor' : 'Variable Labor'} - ${header}`)
    )),
    ...columns.flatMap((label) => (
      itemColumnsForLabel(label).flatMap((column) => metricCsvHeaders(column).map((header) => `${label} - ${header}`))
    )),
  ];
  const bodyRows = rows.map((row) => {
    const childRows = rowsFrom(row[childKey]);
    const byLabel = new Map<string, LaborRecord>(childRows.map((item) => [String(item.label ?? ''), item]));
    return [
      row.week ?? '',
      row.day ?? '',
      row.date ?? '',
      dailyActiveValue(row),
      ...totalColumns.flatMap((column) => metricCsvValues(row, column)),
      ...columns.flatMap((label) => {
        const item = byLabel.get(label);
        return itemColumnsForLabel(label).flatMap((column) => metricCsvValues(item, column));
      }),
    ];
  });

  if (data?.footer?.totals || data?.footer?.by_label) {
    bodyRows.push([
      section === 'variable' ? data.scheduled_days ?? '' : '',
      '',
      'Total',
      '',
      ...totalColumns.flatMap((column) => metricCsvValues(data.footer?.totals, column)),
      ...columns.flatMap((label) => {
        const item = data.footer?.by_label?.[label];
        return itemColumnsForLabel(label).flatMap((column) => metricCsvValues(item, column));
      }),
    ]);
  }

  return { title, headers, rows: bodyRows };
}

function buildFixedAllocationData(report: LaborRecord | null): {
  groups: Array<{ label: string; columns: MetricColumn[] }>;
  rows: LaborRecord[];
} {
  const fixedRows = rowsFrom(recordFrom(report?.fixed_labor).rows);
  const fixedLabor = recordFrom(report?.fixed_labor);
  const fixedSetup = recordFrom(report?.fixed_labor_setup);
  const departmentBudgets = rowsFrom(fixedSetup.department_budgets);
  if (fixedRows.length === 0 || departmentBudgets.length === 0) return { groups: [], rows: [] };

  const budgetByDepartment = new Map(
    departmentBudgets.map((row) => [String(row.department_key ?? ''), toNumber(row.monthly_budget_total)]),
  );
  const allocationWeights = recordFrom(fixedSetup.daily_allocation_weights);
  const fiscalWeeks = toNumber(fixedLabor.fiscal_weeks) || toNumber(fixedSetup.number_of_weeks) || 0;
  const weekdayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const firstRowByDay = new Map<string, LaborRecord>();
  fixedRows.forEach((row) => {
    const day = String(row.weekday ?? '').toUpperCase();
    if (row.active_flag === 1 && day && !firstRowByDay.has(day)) firstRowByDay.set(day, row);
  });

  const rows = weekdayOrder.map((day, weekdayIndex) => {
    const sourceRow = firstRowByDay.get(day) ?? {};
    const row: LaborRecord = { day };
    fixedDepartmentMap.forEach((department) => {
      const amount = toNumber(sourceRow[department.budget]);
      const monthlyBudget = budgetByDepartment.get(department.setupKey) ?? 0;
      const weights = Array.isArray(allocationWeights[department.setupKey])
        ? allocationWeights[department.setupKey] as unknown[]
        : [];
      const templatePercent = weights[weekdayIndex] !== undefined ? toNumber(weights[weekdayIndex]) * 100 : null;
      row[`${department.setupKey}_amount`] = amount;
      row[`${department.setupKey}_percent`] = templatePercent ?? (monthlyBudget > 0 && fiscalWeeks > 0
        ? ((amount * fiscalWeeks) / monthlyBudget) * 100
        : 0);
    });
    return row;
  });
  const groups = fixedDepartmentMap.map((department) => ({
    label: department.label,
    columns: [
      { key: `${department.setupKey}_percent`, label: '%', format: 'percent' },
      { key: `${department.setupKey}_amount`, label: 'AMT', format: 'currency' },
    ] satisfies MetricColumn[],
  }));

  return { groups, rows };
}

function buildFixedAllocationCsvSection(report: LaborRecord | null): CsvSection | null {
  const { groups, rows } = buildFixedAllocationData(report);
  const groupedColumns = groups.flatMap((group) => (
    group.columns.map((column) => ({ groupLabel: group.label, column }))
  ));
  if (rows.length === 0 || groupedColumns.length === 0) return null;
  return {
    title: 'Fixed Allocation % / AMT',
    headers: [
      'Day',
      ...groupedColumns.map(({ groupLabel, column }) => `${groupLabel} - ${column.label}`),
    ],
    rows: [
      ...rows.map((row) => [
        row.day ?? '',
        ...groupedColumns.map(({ column }) => metricCsvValue(row[column.key])),
      ]),
      [
        'Total',
        ...groupedColumns.map(({ column }) => metricFooterCsvValue(rows, column)),
      ],
    ],
  };
}

function buildLaborGridCsvSections({
  gridSection,
  report,
  fixedRows,
  variableRows,
  fixedDetailView,
  variableDetailView,
}: {
  gridSection: LaborGridSection;
  report: LaborRecord | null;
  fixedRows: LaborRecord[];
  variableRows: LaborRecord[];
  fixedDetailView: LaborDetailData | null;
  variableDetailView: LaborDetailData | null;
}): CsvSection[] {
  const showFixed = gridSection === 'fixed' || gridSection === 'all';
  const showVariable = gridSection === 'variable' || gridSection === 'all';
  const sections: Array<CsvSection | null> = [];

  if (showFixed) {
    sections.push(
      buildDetailGridCsvSection('Fixed Labor Daily Grid', 'fixed', fixedDetailView),
      buildFixedAllocationCsvSection(report),
      buildFlatDailyCsvSection('Fixed Debit / Production Support', fixedRows, fixedSupportColumns),
    );
  }
  if (showVariable) {
    sections.push(
      buildGroupedDailyCsvSection('Variable Labor Daily Grid', variableRows, variableSummaryGroups),
      buildFlatDailyCsvSection('Variable Credits / Debits', variableRows, variableAdjustmentColumns),
      buildDetailGridCsvSection('Pack Line Detail Tables', 'variable', variableDetailView, false),
    );
  }

  return sections.filter((section): section is CsvSection => Boolean(section));
}

function toggleMultiSectionSelection(current: string[], option: string): string[] {
  if (option === 'all') return ['all'];
  if (current.includes('all')) return [option];
  const next = current.includes(option)
    ? current.filter((item) => item !== option)
    : [...current, option];
  return next.length > 0 ? next : ['all'];
}

function buildDailyLaborChartRows(report: LaborRecord | null, fixedRows: LaborRecord[], variableRows: LaborRecord[]): DailyLaborChartRow[] {
  const dailyNetMetadata = recordFrom(recordFrom(recordFrom(report?.labor_p3_report).variance_metadata).daily_net_labor_variance_by_date);
  const maxRows = Math.max(fixedRows.length, variableRows.length);
  return Array.from({ length: maxRows }, (_, index) => {
    const fixedRow = fixedRows[index] ?? {};
    const variableRow = variableRows[index] ?? {};
    const workDate = fixedRow.work_date ?? variableRow.work_date;
    const isoDate = typeof workDate === 'string' ? workDate : '';
    return {
      date: formatDateLabel(workDate),
      isoDate,
      fixedSpend: isUnavailableValue(fixedRow.total_fixed_spent) ? null : toNumber(fixedRow.total_fixed_spent),
      fixedBudget: toNumber(fixedRow.total_fixed_budget),
      fixedVariance: isUnavailableValue(fixedRow.total_fixed_variance) ? null : toNumber(fixedRow.total_fixed_variance),
      fixedVarianceMeta: varianceMetadataFor(fixedRow, 'total_fixed_variance'),
      variableSpend: isUnavailableValue(variableRow.total_variable_spent) ? null : toNumber(variableRow.total_variable_spent),
      variableEarned: isUnavailableValue(variableRow.total_variable_earned) ? null : toNumber(variableRow.total_variable_earned),
      variableVariance: isUnavailableValue(variableRow.total_variable_variance) ? null : toNumber(variableRow.total_variable_variance),
      variableVarianceMeta: varianceMetadataFor(variableRow, 'total_variable_variance'),
      netVarianceMeta: varianceMetadataFrom(dailyNetMetadata[isoDate]),
      producedUnits: isUnavailableValue(variableRow.units) ? null : toNumber(variableRow.units),
    };
  });
}

function lineUnitOfMeasure(lineName: string): string {
  return lineName === 'MIXING' ? 'LB' : 'CS';
}

function buildLineLaborChartRows(report: LaborRecord | null, startDate?: string | null, endDate?: string | null): LineLaborChartRow[] {
  const lineTables = recordFrom(report?.line_labor);
  const detailRows = Object.entries(lineTables);
  if (detailRows.length > 0) {
    return detailRows.map(([lineName, table]) => {
      const rows = rowsFrom(recordFrom(table).rows).filter((row) => (
        rowDateInRange(row, startDate, endDate) && !isUnavailableValue(row.variance)
      ));
      const employeeSpend = sumBy(rows, 'employee_spend');
      const tempSpend = sumBy(rows, 'temp_spend');
      const benefits = sumBy(rows, 'benefits');
      const earned = sumBy(rows, 'earned');
      return {
        lineName,
        spend: employeeSpend + benefits + tempSpend,
        earned,
        variance: sumBy(rows, 'variance'),
        varianceMeta: null,
        producedUnits: sumBy(rows, 'units_produced'),
        unitOfMeasure: lineUnitOfMeasure(lineName),
        earnedHours: sumBy(rows, 'earned_hours'),
      };
    });
  }

  const lineTotals = recordFrom(recordFrom(report?.labor_p3_report).line_total_rows);
  return Object.entries(lineTotals).map(([lineName, payload]) => {
    const payloadRecord = recordFrom(payload);
    const values = recordFrom(payloadRecord.values_by_cell);
    const fields = recordFrom(payloadRecord.field_by_cell);
    const derived = recordFrom(payloadRecord.derived_values);
    const employeeSpendValue = lineTotalValue(values, fields, 'employee_spend');
    const tempSpendValue = lineTotalValue(values, fields, 'temp_spend');
    const earnedValue = lineTotalValue(values, fields, 'earned');
    const benefits = toNumber(lineTotalValue(values, fields, 'benefits'));
    const employeeSpend = isUnavailableValue(employeeSpendValue) ? null : toNumber(employeeSpendValue);
    const tempSpend = isUnavailableValue(tempSpendValue) ? null : toNumber(tempSpendValue);
    const earned = isUnavailableValue(earnedValue) ? null : toNumber(earnedValue);
    const spend = employeeSpend === null || tempSpend === null ? null : employeeSpend + benefits + tempSpend;
    return {
      lineName,
      spend: isUnavailableValue(derived.spend) ? spend : toNumber(derived.spend),
      earned: isUnavailableValue(derived.earned) ? earned : toNumber(derived.earned),
      variance: isUnavailableValue(derived.variance) ? null : toNumber(derived.variance),
      varianceMeta: varianceMetadataFrom(recordFrom(payloadRecord.variance_metadata).variance),
      producedUnits: isUnavailableValue(lineTotalValue(values, fields, 'units_produced')) ? null : toNumber(lineTotalValue(values, fields, 'units_produced')),
      unitOfMeasure: lineUnitOfMeasure(lineName),
      earnedHours: isUnavailableValue(lineTotalValue(values, fields, 'earned_hours')) ? null : toNumber(lineTotalValue(values, fields, 'earned_hours')),
    };
  });
}

function dashboardLineRowsFrom(value: unknown): LineLaborChartRow[] {
  return rowsFrom(value).map((row) => ({
    lineName: stringValue(row.name) ?? stringValue(row.lineName) ?? '',
    spend: isUnavailableValue(row.spend) ? null : toNumber(row.spend),
    earned: isUnavailableValue(row.earned) ? null : toNumber(row.earned),
    variance: isUnavailableValue(row.variance) ? null : toNumber(row.variance),
    varianceMeta: null,
    producedUnits: isUnavailableValue(row.produced_units) ? null : toNumber(row.produced_units),
    unitOfMeasure: stringValue(row.unit_of_measure) ?? 'CS',
    earnedHours: isUnavailableValue(row.earned_hours) ? null : toNumber(row.earned_hours),
  })).filter((row) => row.lineName);
}

function buildFixedDepartmentSummaryRows(fixedRows: LaborRecord[], report: LaborRecord | null): FixedDepartmentSummaryRow[] {
  const rollupVarianceMetadata = recordFrom(recordFrom(report?.labor_p3_report).variance_metadata);
  return fixedDepartmentMap.map((department) => {
    const spend = fixedRows.reduce((total, row) => (
      isUnavailableValue(row[department.variance])
        ? total
        : total + toNumber(row[department.emp]) + (department.temps ? toNumber(row[department.temps]) : 0)
    ), 0);
    const budget = sumByWhen(fixedRows, department.budget, (row) => !isUnavailableValue(row[department.variance]));
    const variance = sumBy(fixedRows, department.variance);
    return {
      label: department.label,
      spend,
      budget,
      variance,
      varianceMeta: varianceMetadataFrom(rollupVarianceMetadata[department.variance]),
      budgetAvailable: budget > 0,
    };
  }).filter((row) => row.spend !== 0 || row.budget !== 0 || row.variance !== 0);
}

function dashboardDepartmentRowsFrom(value: unknown): FixedDepartmentSummaryRow[] {
  return rowsFrom(value).map((row) => ({
    label: stringValue(row.name) ?? stringValue(row.label) ?? '',
    spend: toNumber(row.spend),
    budget: toNumber(row.budget),
    variance: toNumber(row.variance),
    varianceMeta: null,
    budgetAvailable: Boolean(row.budget_available),
  })).filter((row) => row.label);
}

function buildDailyExceptionRows(dailyRows: DailyLaborChartRow[]): DailyExceptionRow[] {
  return dailyRows
    .map((row) => {
      const spend = row.fixedSpend === null || row.variableSpend === null ? null : row.fixedSpend + row.variableSpend;
      const plannedEarned = row.variableEarned === null ? null : row.fixedBudget + row.variableEarned;
      const variance = plannedEarned === null || spend === null ? null : plannedEarned - spend;
      return {
        date: row.date,
        spend,
        plannedEarned,
        variance,
        varianceMeta: row.netVarianceMeta,
        units: row.producedUnits,
      };
    })
    .filter((row) => row.date && row.variance !== null && (row.spend !== 0 || row.plannedEarned !== 0 || row.units !== 0))
    .sort((a, b) => Math.abs(b.variance ?? 0) - Math.abs(a.variance ?? 0))
    .slice(0, 6);
}

function dashboardExceptionRowsFrom(value: unknown): DailyExceptionRow[] {
  return rowsFrom(value).map((row) => ({
    date: stringValue(row.display_date) ?? formatDateLabel(row.date),
    isoDate: stringValue(row.date) ?? undefined,
    spend: toNumber(row.spend),
    plannedEarned: toNumber(row.planned_value),
    variance: isUnavailableValue(row.variance) ? null : toNumber(row.variance),
    varianceMeta: null,
    units: toNumber(row.units),
  })).filter((row) => row.date && row.variance !== null);
}

function hasChartValues<T extends object>(rows: T[], keys: string[]): boolean {
  return rows.some((row) => keys.some((key) => toNumber(row[key as keyof T]) !== 0));
}

function getDetailChunkSize(section: 'fixed' | 'variable', contentWidth: number): number {
  if (section === 'fixed') {
    if (contentWidth >= 1760) return 4;
    if (contentWidth >= 1040) return 3;
    return 2;
  }
  return 1;
}

function useElementWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const updateWidth = () => setWidth(Math.round(element.getBoundingClientRect().width));
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

function SectionPicker<T extends string>({
  label,
  options,
  value,
  onChange,
  action,
}: {
  label: string;
  options: SectionOption<T>[];
  value: T;
  onChange: (value: T) => void;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="flex flex-col gap-3 rounded-md border border-[#E6DED6] bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={`h-9 rounded-md border px-3 text-sm font-medium transition ${value === option.id ? 'border-[#6E3450] bg-[#6E3450] text-white shadow-sm' : 'border-[#D9D0C8] bg-white text-[#4D4649] hover:bg-[#F5EFE8]'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
        {action}
      </div>
    </section>
  );
}

function MultiSectionPicker({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: SectionOption<string>[];
  value: string[];
  onChange: (value: string[]) => void;
}): React.ReactElement {
  return (
    <section className="flex flex-col gap-3 rounded-md border border-[#E6DED6] bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(toggleMultiSectionSelection(value, option.id))}
              className={`h-9 rounded-md border px-3 text-sm font-medium transition ${active ? 'border-[#6E3450] bg-[#6E3450] text-white shadow-sm' : 'border-[#D9D0C8] bg-white text-[#4D4649] hover:bg-[#F5EFE8]'}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function formatNullableMetric(value: unknown, column: MetricColumn): string {
  return formatMetric(value, column);
}

function metricFooterValue(rows: LaborRecord[], column: MetricColumn): string {
  if (column.summary === 'blank' || column.format === 'rate') return 'N/A';
  return formatMetric(sumBy(rows, column.key), column);
}

function metricColumnSpan(column: MetricColumn): number {
  return column.variance ? 3 : 1;
}

function metricColumnsSpan(columns: MetricColumn[]): number {
  return columns.reduce((total, column) => total + metricColumnSpan(column), 0);
}

function metricTableMinWidth(baseColumns: number, columns: MetricColumn[], minimum: number): number {
  return Math.max(minimum, (baseColumns * 88) + (metricColumnsSpan(columns) * 112));
}

function metricHeaderLabels(column: MetricColumn): string[] {
  if (!column.variance) return [column.label];
  return [varianceAmountHeader(column), 'Variance (percentage)', 'Favourable/Unfavourable'];
}

function MetricHeaderLabel({ label }: { label: string }): React.ReactElement {
  if (label === 'Favourable/Unfavourable') {
    return (
      <span className="inline-flex flex-col items-end leading-tight">
        <span>Favourable/</span>
        <span>Unfavourable</span>
      </span>
    );
  }
  return <span>{label}</span>;
}

function dailyWeekValue(row: LaborRecord): unknown {
  return row.week_number ?? row.week ?? '';
}

function dailyDayValue(row: LaborRecord): unknown {
  return row.weekday ?? row.day ?? '';
}

function dailyDateValue(row: LaborRecord): string {
  return formatDateLabel(row.work_date ?? row.iso_date ?? row.date);
}

function dailyActiveValue(row: LaborRecord): unknown {
  return row.active_flag ?? row.active ?? '';
}

function metricCellClass(column: MetricColumn, value: unknown, strongBorder = false, metadata?: VarianceMetadata | null): string {
  return `border-r px-2 py-1 text-right tabular-nums ${column.variance ? varianceClass(value, metadata) : 'border-[#D8D0C9]'} ${strongBorder ? 'border-[#1F1A20]' : ''}`;
}

function metricVarianceMetadata(row: LaborRecord | undefined | null, column: MetricColumn): VarianceMetadata | null {
  if (!column.variance) return null;
  return varianceMetadataFor(row, column.key);
}

function varianceBasisForRow(row: LaborRecord | undefined | null, column: MetricColumn): unknown {
  if (!row || !column.variance) return undefined;
  const basisKeys: Record<string, string[]> = {
    variance: ['earned', 'budget'],
    total_variable_variance: ['total_variable_earned'],
    variance_per_case: ['earned_per_case'],
    variance_per_unit: ['earned_per_unit'],
    hours_variance: ['earned_hours'],
    hours_var: ['earned_hours'],
    packaging_variance: ['packaging_earned'],
    mixing_variance: ['mixing_earned'],
  };
  const candidates = basisKeys[column.key] ?? [];
  const basisKey = candidates.find((key) => row[key] !== undefined && row[key] !== null && row[key] !== '');
  return basisKey ? row[basisKey] : undefined;
}

function metricFooterBasis(rows: LaborRecord[], column: MetricColumn): unknown {
  const basisKey = (() => {
    if (column.key === 'variance') {
      if (rows.some((row) => row.earned !== undefined)) return 'earned';
      if (rows.some((row) => row.budget !== undefined)) return 'budget';
      return null;
    }
    const basisMap: Record<string, string> = {
      total_variable_variance: 'total_variable_earned',
      packaging_variance: 'packaging_earned',
      mixing_variance: 'mixing_earned',
      hours_variance: 'earned_hours',
      hours_var: 'earned_hours',
      variance_per_case: 'earned_per_case',
      variance_per_unit: 'earned_per_unit',
    };
    return basisMap[column.key] ?? null;
  })();
  return basisKey ? sumBy(rows, basisKey) : undefined;
}

function VarianceStatusContent({
  value,
  metadata,
}: {
  value: unknown;
  metadata?: VarianceMetadata | null;
}): React.ReactElement {
  return (
    <span className="inline-flex items-center justify-end gap-1 font-semibold">
      <VarianceIcon value={value} metadata={metadata} className="h-3 w-3" />
      <span>{varianceStatusLabel(value, metadata)}</span>
    </span>
  );
}

function MetricHeaderCells({
  column,
  isLast,
  keyPrefix,
}: {
  column: MetricColumn;
  isLast: boolean;
  keyPrefix: string;
}): React.ReactElement {
  const labels = metricHeaderLabels(column);
  return (
    <>
      {labels.map((label, index) => (
        <th
          key={`${keyPrefix}-${column.key}-${index}`}
          className={`border-b border-r bg-[#D7D7D7] px-2 py-1 text-right font-semibold leading-tight ${isLast && index === labels.length - 1 ? 'border-[#1F1A20]' : 'border-[#B9B0AA]'}`}
        >
          <MetricHeaderLabel label={label} />
        </th>
      ))}
    </>
  );
}

function MetricDataCells({
  row,
  column,
  isLast,
  keyPrefix,
}: {
  row: LaborRecord | undefined | null;
  column: MetricColumn;
  isLast: boolean;
  keyPrefix: string;
}): React.ReactElement {
  const value = row?.[column.key];
  const metadata = metricVarianceMetadata(row, column);
  if (!column.variance) {
    return (
      <td key={`${keyPrefix}-${column.key}`} className={metricCellClass(column, value, isLast, metadata)}>
        {formatNullableMetric(value, column)}
      </td>
    );
  }

  const cellClass = (strongBorder = false) => metricCellClass(column, value, strongBorder, metadata);
  return (
    <>
      <td key={`${keyPrefix}-${column.key}-amount`} className={cellClass(false)}>
        {formatNullableMetric(value, column)}
      </td>
      <td key={`${keyPrefix}-${column.key}-pct`} className={cellClass(false)}>
        {variancePctLabel(metadata, value, varianceBasisForRow(row, column)) ?? 'N/A'}
      </td>
      <td key={`${keyPrefix}-${column.key}-status`} className={cellClass(isLast)}>
        <VarianceStatusContent value={value} metadata={metadata} />
      </td>
    </>
  );
}

function MetricFooterCells({
  rows,
  column,
  isLast,
  keyPrefix,
}: {
  rows: LaborRecord[];
  column: MetricColumn;
  isLast: boolean;
  keyPrefix: string;
}): React.ReactElement {
  const value = column.summary === 'blank' || column.format === 'rate' ? '' : sumBy(rows, column.key);
  if (!column.variance) {
    return (
      <td key={`${keyPrefix}-${column.key}`} className={metricCellClass(column, value, isLast)}>
        {value === '' ? 'N/A' : formatMetric(value, column)}
      </td>
    );
  }

  const basis = metricFooterBasis(rows, column);
  const cellClass = (strongBorder = false) => metricCellClass(column, value, strongBorder);
  return (
    <>
      <td key={`${keyPrefix}-${column.key}-amount`} className={cellClass(false)}>
        {value === '' ? 'N/A' : formatMetric(value, column)}
      </td>
      <td key={`${keyPrefix}-${column.key}-pct`} className={cellClass(false)}>
        {value === '' ? 'N/A' : variancePctLabel(null, value, basis) ?? 'N/A'}
      </td>
      <td key={`${keyPrefix}-${column.key}-status`} className={cellClass(isLast)}>
        {value === '' ? 'N/A' : (
          <VarianceStatusContent value={value} />
        )}
      </td>
    </>
  );
}

function LaborFlatDailyTable({
  title,
  badge,
  rows,
  columns,
  minWidth = 760,
}: {
  title: string;
  badge: string;
  rows: LaborRecord[];
  columns: MetricColumn[];
  minWidth?: number;
}): React.ReactElement | null {
  if (rows.length === 0 || columns.length === 0) return null;
  const tableMinWidth = metricTableMinWidth(4, columns, minWidth);

  return (
    <section className="overflow-hidden rounded-md border border-[#D8D0C9] bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#D8D0C9] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">{title}</h2>
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#7B6F73]">{badge}</span>
      </div>
      <div className="overflow-x-auto p-3">
        <table className="w-full border-collapse text-[clamp(10px,0.58vw,12px)] leading-tight" style={{ minWidth: tableMinWidth }}>
          <thead>
            <tr className="bg-[#F7F1EA] text-xs font-semibold uppercase tracking-[0.08em] text-[#6B5E62]">
              <th className="border-b border-r border-[#B9B0AA] px-2 py-2 text-left">Week</th>
              <th className="border-b border-r border-[#B9B0AA] px-2 py-2 text-left">Day</th>
              <th className="border-b border-r border-[#B9B0AA] px-2 py-2 text-left">Date</th>
              <th className="border-b border-r border-[#B9B0AA] px-2 py-2 text-right">Active</th>
              {columns.map((column, index) => (
                <MetricHeaderCells key={column.key} column={column} isLast={index === columns.length - 1} keyPrefix="flat-header" />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={String(row.work_date ?? row.iso_date ?? rowIndex)} className={rowIndex % 7 === 0 ? 'border-t-2 border-t-[#1F1A20]' : undefined}>
                <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-center font-semibold">{String(dailyWeekValue(row) ?? '')}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1 font-medium">{String(dailyDayValue(row) ?? '')}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1">{dailyDateValue(row)}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1 text-right tabular-nums">{String(dailyActiveValue(row) ?? '')}</td>
                {columns.map((column, index) => (
                  <MetricDataCells key={`${rowIndex}-${column.key}`} row={row} column={column} isLast={index === columns.length - 1} keyPrefix={`${rowIndex}`} />
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-t-[#1F1A20] bg-[#FAF7F1] font-semibold">
              <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1" />
              <td className="border-r border-[#D8D0C9] px-2 py-1" />
              <td className="border-r border-[#D8D0C9] px-2 py-1">Total</td>
              <td className="border-r border-[#D8D0C9] px-2 py-1" />
              {columns.map((column, index) => (
                <MetricFooterCells key={`footer-${column.key}`} rows={rows} column={column} isLast={index === columns.length - 1} keyPrefix="footer" />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LaborGroupedDailyTable({
  title,
  badge,
  rows,
  groups,
  minWidth = 1320,
}: {
  title: string;
  badge: string;
  rows: LaborRecord[];
  groups: Array<{ label: string; columns: MetricColumn[] }>;
  minWidth?: number;
}): React.ReactElement | null {
  const allColumns = groups.flatMap((group) => group.columns);
  if (rows.length === 0 || allColumns.length === 0) return null;
  const tableMinWidth = metricTableMinWidth(4, allColumns, minWidth);

  return (
    <section className="overflow-hidden rounded-md border border-[#D8D0C9] bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#D8D0C9] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">{title}</h2>
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#7B6F73]">{badge}</span>
      </div>
      <div className="overflow-x-auto p-3">
        <table className="w-full border-collapse text-[clamp(10px,0.58vw,12px)] leading-tight" style={{ minWidth: tableMinWidth }}>
          <thead className="bg-white">
            <tr>
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Week</th>
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Day</th>
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Date</th>
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-right font-semibold text-[#1F1A20]">Active</th>
              {groups.map((group) => (
                <th key={group.label} colSpan={metricColumnsSpan(group.columns)} className="border-b border-r border-[#1F1A20] bg-[#F5F5F5] px-3 py-2 text-center font-bold uppercase text-[#1F1A20]">
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              <th className="border-b border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-left font-semibold text-[#1F1A20]" />
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
              {groups.map((group) => (
                <React.Fragment key={`${group.label}-headers`}>
                  {group.columns.map((column, index) => (
                    <MetricHeaderCells key={`${group.label}-${column.key}`} column={column} isLast={index === group.columns.length - 1} keyPrefix={group.label} />
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={String(row.work_date ?? row.iso_date ?? rowIndex)} className={rowIndex % 7 === 0 ? 'border-t-2 border-t-[#1F1A20]' : undefined}>
                <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-center font-semibold">{String(dailyWeekValue(row) ?? '')}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1 font-medium">{String(dailyDayValue(row) ?? '')}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1">{dailyDateValue(row)}</td>
                <td className="border-r border-[#D8D0C9] px-2 py-1 text-right tabular-nums">{String(dailyActiveValue(row) ?? '')}</td>
                {groups.map((group) => (
                  <React.Fragment key={`${rowIndex}-${group.label}`}>
                    {group.columns.map((column, index) => (
                      <MetricDataCells key={`${rowIndex}-${group.label}-${column.key}`} row={row} column={column} isLast={index === group.columns.length - 1} keyPrefix={`${rowIndex}-${group.label}`} />
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-t-[#1F1A20] bg-[#FAF7F1] font-semibold">
              <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1" />
              <td className="border-r border-[#D8D0C9] px-2 py-1" />
              <td className="border-r border-[#D8D0C9] px-2 py-1">Total</td>
              <td className="border-r border-[#D8D0C9] px-2 py-1" />
              {groups.map((group) => (
                <React.Fragment key={`footer-${group.label}`}>
                  {group.columns.map((column, index) => (
                    <MetricFooterCells key={`footer-${group.label}-${column.key}`} rows={rows} column={column} isLast={index === group.columns.length - 1} keyPrefix={`footer-${group.label}`} />
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FixedAllocationTable({ report }: { report: LaborRecord | null }): React.ReactElement | null {
  const { groups, rows } = buildFixedAllocationData(report);
  if (rows.length === 0 || groups.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-md border border-[#D8D0C9] bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#D8D0C9] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Fixed Allocation % / AMT</h2>
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#7B6F73]">Allocation</span>
      </div>
      <div className="overflow-x-auto p-3">
        <table className="w-full min-w-[980px] border-collapse text-[clamp(10px,0.58vw,12px)] leading-tight">
          <thead className="bg-white">
            <tr>
              <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Day</th>
              {groups.map((group) => (
                <th key={group.label} colSpan={group.columns.length} className="border-b border-r border-[#1F1A20] bg-[#F5F5F5] px-3 py-2 text-center font-bold uppercase text-[#1F1A20]">
                  {group.label}
                </th>
              ))}
            </tr>
            <tr>
              <th className="border-b border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1" />
              {groups.map((group) => (
                <React.Fragment key={`${group.label}-headers`}>
                  {group.columns.map((column, index) => (
                    <th key={`${group.label}-${column.key}`} className={`border-b border-r bg-[#D7D7D7] px-2 py-1 text-right font-semibold ${index === group.columns.length - 1 ? 'border-[#1F1A20]' : 'border-[#B9B0AA]'}`}>{column.label}</th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.day)}>
                <td className="border-r border-[#D8D0C9] px-2 py-1 font-medium">{String(row.day)}</td>
                {groups.map((group) => (
                  <React.Fragment key={`${row.day}-${group.label}`}>
                    {group.columns.map((column, index) => (
                      <td key={`${row.day}-${group.label}-${column.key}`} className={metricCellClass(column, row[column.key], index === group.columns.length - 1, metricVarianceMetadata(row, column))}>
                        {formatNullableMetric(row[column.key], column)}
                      </td>
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            ))}
            <tr className="border-t-2 border-t-[#1F1A20] bg-[#FAF7F1] font-semibold">
              <td className="border-r border-[#D8D0C9] px-2 py-1">Total</td>
              {groups.map((group) => (
                <React.Fragment key={`footer-${group.label}`}>
                  {group.columns.map((column, index) => (
                    <td key={`footer-${group.label}-${column.key}`} className={metricCellClass(column, sumBy(rows, column.key), index === group.columns.length - 1)}>
                      {metricFooterValue(rows, column)}
                    </td>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LaborDetailGrid({
  title,
  section,
  contentWidth,
  data,
  showTotals = true,
  showFooter = true,
}: LaborDetailGridProps & { showTotals?: boolean; showFooter?: boolean }): React.ReactElement | null {
  const columns = Array.isArray(data?.columns) ? data.columns.map((label) => String(label ?? '')) : [];
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  if (columns.length === 0 || rows.length === 0) return null;

  const childKey = section === 'fixed' ? 'departments' : 'lines';
  const totalColumns = showTotals ? (section === 'fixed' ? fixedTotalColumns : variableTotalColumns) : [];
  const itemColumnsForLabel = (label: string) => (
    section === 'fixed' ? fixedItemColumns : variableLineItemColumns(label)
  );
  const chunkSize = getDetailChunkSize(section, contentWidth);
  const columnChunks = columns.reduce<string[][]>((chunks, label, index) => {
    if (index % chunkSize === 0) chunks.push([]);
    chunks[chunks.length - 1].push(label);
    return chunks;
  }, []);

  return (
    <section className="overflow-hidden rounded-md border border-[#D8D0C9] bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#D8D0C9] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">{title}</h2>
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#7B6F73]">
          {section === 'fixed' ? 'Fixed' : 'Variable'}
        </span>
      </div>
      <div className="flex flex-col gap-4 overflow-x-auto p-3">
        {columnChunks.map((chunk, chunkIndex) => {
          const chunkTotalColumns = chunkIndex === 0 ? totalColumns : [];
          return (
          <table
            key={`${section}-${chunkIndex}`}
            className="w-full table-fixed border-collapse text-[clamp(10px,0.58vw,12px)] leading-tight"
            style={{
              minWidth: metricTableMinWidth(
                4,
                [
                  ...chunkTotalColumns,
                  ...chunk.flatMap((label) => itemColumnsForLabel(label)),
                ],
                section === 'fixed' ? 1180 : 1560,
              ),
            }}
          >
            <thead className="bg-white">
              <tr>
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Week</th>
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Day</th>
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-left font-semibold text-[#1F1A20]">Date</th>
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-2 text-right font-semibold text-[#1F1A20]">Active</th>
                {chunkTotalColumns.length > 0 ? (
                  <th colSpan={metricColumnsSpan(chunkTotalColumns)} className="border-b border-r border-[#1F1A20] bg-[#087A22] px-3 py-2 text-center font-bold uppercase text-white">
                    Total {section === 'fixed' ? 'Fixed Labor' : 'Variable Labor'}
                  </th>
                ) : null}
                {chunk.map((label) => (
                  <th key={label} colSpan={metricColumnsSpan(itemColumnsForLabel(label))} className="border-b border-r border-[#1F1A20] bg-[#F5F5F5] px-3 py-2 text-center font-bold uppercase text-[#1F1A20]">
                    {label}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="border-b border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-left font-semibold text-[#1F1A20]" />
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
                <th className="border-b border-r border-[#B9B0AA] bg-[#F7F1EA] px-2 py-1" />
                {chunkTotalColumns.map((column, index) => (
                  <MetricHeaderCells key={`total-${column.key}`} column={column} isLast={index === chunkTotalColumns.length - 1} keyPrefix="total" />
                ))}
                {chunk.map((label) => (
                  <React.Fragment key={`${label}-headers`}>
                    {itemColumnsForLabel(label).map((column, index, lineColumns) => (
                      <MetricHeaderCells key={`${label}-${column.key}`} column={column} isLast={index === lineColumns.length - 1} keyPrefix={label} />
                    ))}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const childRows = rowsFrom(row[childKey]);
                const byLabel = new Map<string, LaborRecord>(childRows.map((item) => [String(item.label ?? ''), item]));
                return (
                  <tr key={String(row.iso_date ?? rowIndex)} className={rowIndex % 7 === 0 ? 'border-t-2 border-t-[#1F1A20]' : undefined}>
                    <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-center font-semibold">{String(row.week ?? '')}</td>
                    <td className="border-r border-[#D8D0C9] px-2 py-1 font-medium">{String(row.day ?? '')}</td>
                    <td className="border-r border-[#D8D0C9] px-2 py-1">{String(row.date ?? '')}</td>
                    <td className="border-r border-[#D8D0C9] px-2 py-1 text-right tabular-nums">{String(dailyActiveValue(row) ?? '')}</td>
                    {chunkTotalColumns.map((column, index) => (
                      <MetricDataCells key={`row-total-${column.key}`} row={row} column={column} isLast={index === chunkTotalColumns.length - 1} keyPrefix={`row-${rowIndex}-total`} />
                    ))}
                    {chunk.map((label) => {
                      const item = byLabel.get(label);
                      return (
                        <React.Fragment key={`${String(row.iso_date ?? rowIndex)}-${label}`}>
                          {itemColumnsForLabel(label).map((column, index, lineColumns) => (
                            <MetricDataCells key={`${label}-${column.key}`} row={item} column={column} isLast={index === lineColumns.length - 1} keyPrefix={`row-${rowIndex}-${label}`} />
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
              {showFooter && data?.footer?.totals ? (
                <tr className="border-t-2 border-t-[#1F1A20] bg-[#FAF7F1] font-semibold">
                  <td className="border-r border-[#B9B0AA] bg-[#FFF200] px-2 py-1 text-center">{section === 'fixed' ? '' : data.scheduled_days}</td>
                  <td className="border-r border-[#D8D0C9] px-2 py-1" />
                  <td className="border-r border-[#D8D0C9] px-2 py-1">Total</td>
                  <td className="border-r border-[#D8D0C9] px-2 py-1" />
                  {chunkTotalColumns.map((column, index) => (
                    <MetricDataCells key={`footer-total-${column.key}`} row={data.footer?.totals} column={column} isLast={index === chunkTotalColumns.length - 1} keyPrefix="footer-total" />
                  ))}
                  {chunk.map((label) => {
                    const item = data.footer?.by_label?.[label];
                    return (
                      <React.Fragment key={`footer-${label}`}>
                        {itemColumnsForLabel(label).map((column, index, lineColumns) => (
                          <MetricDataCells key={`footer-${label}-${column.key}`} row={item} column={column} isLast={index === lineColumns.length - 1} keyPrefix={`footer-${label}`} />
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ) : null}
            </tbody>
          </table>
          );
        })}
      </div>
    </section>
  );
}

function LaborKpiSection({ metrics, fiscalYearPeriod, loading = false }: { metrics: LaborKpiCardData[]; fiscalYearPeriod: string; loading?: boolean }): React.ReactElement | null {
  if (metrics.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
      <header className="flex items-center gap-2.5 border-b border-[#EDE5D8] px-5 pb-3 pt-4 md:px-6">
        <span className="font-display text-[20px] leading-none text-[#67ADCA]">§</span>
        <span className="font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7B6B72]">
          Key Numbers
        </span>
      </header>
      <div className="grid gap-3 px-5 py-4 sm:grid-cols-2 md:grid-cols-3 md:px-6 xl:grid-cols-6">
        {metrics.map((metric) => (
          <div key={metric.id} className="min-w-0 overflow-hidden rounded-[10px] border border-[#EDE5D8] bg-white p-4 xl:p-3">
            <div className="flex min-h-[5.75rem] flex-col justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72] xl:text-[10px]">{metric.label}</p>
                <p className="mt-1 text-xs text-[#9A8E92]">{fiscalYearPeriod}</p>
              </div>
              <div>
                <p className="font-display text-3xl leading-none text-[#1F1A20] xl:text-2xl">
                  {loading ? '--' : metric.format === 'currency' ? formatCurrency(metric.value) : metric.format === 'decimal' ? formatDecimal(metric.value) : formatNumber(metric.value)}
                </p>
                <div className="mt-2">
                  {loading ? (
                    <p className="inline-flex rounded-full border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-1 text-xs font-medium text-[#7B6B72]">
                      --
                    </p>
                  ) : metric.variance || metric.varianceMeta ? (
                    <VariancePill value={metric.value} metadata={metric.varianceMeta} basis={metric.varianceBasis} />
                  ) : (
                    <p className="inline-flex rounded-full border border-[#C4D8CB] bg-[#EDF7EF] px-3 py-1 text-xs font-medium text-[#5E8C67]">
                      {metric.helper}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PeriodStorySection({
  netVariance,
  netVarianceMeta,
  narrative,
  loading,
  reportLoading = false,
  error,
}: {
  netVariance: number;
  netVarianceMeta?: VarianceMetadata | null;
  narrative: { main: string; details: string; recommendations?: string; context?: string; word_counts?: LaborRecord } | null;
  loading: boolean;
  reportLoading?: boolean;
  error: string | null;
}): React.ReactElement | null {
  const main = narrative?.main.trim() ?? '';
  const details = narrative?.details.trim() ?? '';
  const recommendations = narrative?.recommendations?.trim() ?? '';
  const context = narrative?.context?.trim() ?? '';

  return (
    <section className="rounded-md border border-[#EDE6E1] bg-white px-5 py-5 shadow-sm md:px-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7B6B72]">Summary</p>
      {loading ? (
        <p className="mt-3 flex items-center gap-2 text-sm font-medium text-[#6B5E62]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Generating labor performance story...
        </p>
      ) : reportLoading ? (
        <p className="mt-3 text-sm font-medium text-[#6B5E62]">Summary will appear after the report loads.</p>
      ) : error ? (
        <p className="mt-3 text-sm font-medium text-[#B42318]">{error}</p>
      ) : main ? (
        <div className="mt-3 space-y-3">
          <p className="text-sm font-semibold leading-6 text-[#4D4649]">{main}</p>
          {details ? (
            <p className="text-sm font-semibold leading-6 text-[#4D4649]">{details}</p>
          ) : null}
          {recommendations ? (
            <p className="text-sm font-semibold leading-6 text-[#4D4649]">{recommendations}</p>
          ) : null}
          {context ? (
            <p className="text-sm font-semibold leading-6 text-[#4D4649]">{context}</p>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-sm font-medium text-[#6B5E62]">Labor performance story is not available.</p>
      )}
      <div className="mt-4 border-t border-[#EFE7DF] pt-3">
        {reportLoading ? (
          <p className="inline-flex rounded-full border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-1 text-xs font-medium text-[#7B6B72]">
            --
          </p>
        ) : (
          <VariancePill value={netVariance} metadata={netVarianceMeta} />
        )}
      </div>
    </section>
  );
}

function ChartPanel({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">{title}</h2>
      </header>
      <div className="h-72 px-3 py-4">
        {children}
      </div>
    </section>
  );
}

function chartPayloadRows(payload: unknown): Array<Record<string, unknown>> {
  return Array.isArray(payload) ? payload.filter(isRecord) : [];
}

function chartTooltipRowMetadata(item: LaborRecord, row: LaborRecord): VarianceMetadata | null {
  const dataKey = String(item.dataKey ?? '');
  if (dataKey === 'fixedVariance') return varianceMetadataFrom(row.fixedVarianceMeta);
  if (dataKey === 'variableVariance') return varianceMetadataFrom(row.variableVarianceMeta);
  if (dataKey === 'variance') return varianceMetadataFrom(row.varianceMeta);
  return null;
}

function VarianceTrendTooltip({ active, payload, label }: { active?: boolean; payload?: unknown; label?: unknown }): React.ReactElement | null {
  const items = chartPayloadRows(payload);
  if (!active || items.length === 0) return null;
  const row = recordFrom(items[0]?.payload);
  return (
    <div className="rounded-md border border-[#EDE5D8] bg-white px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-semibold text-[#1F1A20]">{String(label ?? '')}</p>
      <div className="space-y-1">
        {items.map((item) => {
          const metadata = chartTooltipRowMetadata(item, row);
          const value = item.value;
          return (
            <div key={String(item.dataKey ?? item.name ?? '')} className="flex items-center justify-between gap-4">
              <span className="text-[#6B5E62]">{String(item.name ?? '')}</span>
              <span className={`font-semibold tabular-nums ${varianceTextClass(value, metadata)}`}>
                {formatCurrency(value)} {varianceStatusLabel(value, metadata)}
                {variancePctLabel(metadata) ? ` (${variancePctLabel(metadata)})` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LineTotalsTooltip({ active, payload, label }: { active?: boolean; payload?: unknown; label?: unknown }): React.ReactElement | null {
  const items = chartPayloadRows(payload);
  if (!active || items.length === 0) return null;
  const row = recordFrom(items[0]?.payload);
  const variance = row.variance;
  const metadata = varianceMetadataFrom(row.varianceMeta);
  return (
    <div className="rounded-md border border-[#EDE5D8] bg-white px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-semibold text-[#1F1A20]">{String(label ?? '')}</p>
      <div className="space-y-1">
        {items.map((item) => (
          <div key={String(item.dataKey ?? item.name ?? '')} className="flex items-center justify-between gap-4">
            <span className="text-[#6B5E62]">{String(item.name ?? '')}</span>
            <span className="font-semibold tabular-nums text-[#1F1A20]">
              {formatCurrency(item.value)}
            </span>
          </div>
        ))}
        <div className="border-t border-[#EFE7DF] pt-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[#6B5E62]">Variance</span>
            <span className={`font-semibold tabular-nums ${varianceTextClass(variance, metadata)}`}>
              {formatCurrency(variance)} {varianceStatusLabel(variance, metadata)}
              {variancePctLabel(metadata) ? ` (${variancePctLabel(metadata)})` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LaborChartsSection({ dailyRows, lineRows }: { dailyRows: DailyLaborChartRow[]; lineRows: LineLaborChartRow[] }): React.ReactElement | null {
  const showDailySpend = hasChartValues(dailyRows, ['fixedSpend', 'fixedBudget', 'variableSpend', 'variableEarned']);
  const showDailyVariance = hasChartValues(dailyRows, ['fixedVariance', 'variableVariance']);
  const showLineTotals = hasChartValues(lineRows, ['spend', 'earned', 'variance']);
  if (!showDailySpend && !showDailyVariance && !showLineTotals) return null;

  const dailyTickInterval = Math.max(0, Math.ceil(dailyRows.length / 10) - 1);

  return (
    <section className="grid gap-4 xl:grid-cols-2">
      {showDailySpend ? (
        <ChartPanel title="Daily Labor Spend vs Earned">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyRows} margin={{ top: 8, right: 18, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#EFE7DF" vertical={false} />
              <XAxis dataKey="date" interval={dailyTickInterval} tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} />
              <YAxis tickFormatter={formatCompactCurrency} tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} width={64} />
              <Tooltip formatter={(value: unknown, name: unknown) => [formatCurrency(value), String(name)]} labelStyle={{ color: '#1F1A20' }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="fixedSpend" name="Fixed Spend" fill="#6E3450" radius={[3, 3, 0, 0]} />
              <Bar dataKey="fixedBudget" name="Fixed Budget" fill="#B69BA7" radius={[3, 3, 0, 0]} />
              <Bar dataKey="variableSpend" name="Variable Spend" fill="#4E7C89" radius={[3, 3, 0, 0]} />
              <Bar dataKey="variableEarned" name="Variable Earned" fill="#8FB8C2" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      ) : null}

      {showDailyVariance ? (
        <ChartPanel title="Daily Labor Variance Trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyRows} margin={{ top: 8, right: 18, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#EFE7DF" vertical={false} />
              <XAxis dataKey="date" interval={dailyTickInterval} tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} />
              <YAxis tickFormatter={formatCompactCurrency} tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} width={64} />
              <Tooltip content={<VarianceTrendTooltip />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#B9B0AA" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="fixedVariance" name="Fixed Variance" stroke="#6E3450" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="variableVariance" name="Variable Variance" stroke="#087A22" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>
      ) : null}

      {showLineTotals ? (
        <div className="xl:col-span-2">
          <ChartPanel title="Line Labor Totals">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={lineRows} margin={{ top: 8, right: 18, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#EFE7DF" vertical={false} />
                <XAxis dataKey="lineName" tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} interval={0} />
                <YAxis yAxisId="currency" tickFormatter={formatCompactCurrency} tickLine={false} axisLine={false} tick={{ fill: '#6B5E62', fontSize: 11 }} width={64} />
                <Tooltip content={<LineTotalsTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="currency" dataKey="spend" name="Spend" fill="#6E3450" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="currency" dataKey="earned" name="Earned" fill="#4E7C89" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </div>
      ) : null}
    </section>
  );
}

function PerformanceMetricStrip({
  fixedBudget,
  variableEarned,
  fixedSpend,
  variableSpend,
  netVariance,
  producedUnits,
  netVarianceMeta,
  loading = false,
}: {
  fixedBudget: number;
  variableEarned: number;
  fixedSpend: number;
  variableSpend: number;
  netVariance: number;
  producedUnits: number;
  netVarianceMeta?: VarianceMetadata | null;
  loading?: boolean;
}): React.ReactElement {
  const plannedEarned = fixedBudget + variableEarned;
  const laborSpend = fixedSpend + variableSpend;
  const variance = netVariance;
  const spendPerUnit = producedUnits > 0 ? laborSpend / producedUnits : 0;
  const earnedPerUnit = producedUnits > 0 ? plannedEarned / producedUnits : 0;
  const variableEarnedPerUnit = producedUnits > 0 ? variableEarned / producedUnits : 0;
  const metrics = [
    { label: 'Budget + Earned Labor', value: formatCurrency(plannedEarned), helper: 'comparable actual rows' },
    { label: 'Fixed Labor Spend', value: formatCurrency(fixedSpend), helper: `${formatCurrency(fixedBudget)} fixed budget` },
    { label: 'Variable Labor Spend', value: formatCurrency(variableSpend), helper: `${formatCurrency(variableEarned)} earned labor` },
    { label: 'Net Labor Variance', value: formatCurrency(variance), helper: varianceStatusLabel(variance, netVarianceMeta), varianceMeta: netVarianceMeta, variance: true, varianceBasis: plannedEarned },
    { label: 'Spend / Unit', value: formatRate(spendPerUnit), helper: `${formatRate(earnedPerUnit)} budget + earned / unit` },
    { label: 'Variable Earned / Case', value: formatRate(variableEarnedPerUnit), helper: 'standard labor value per case' },
  ];

  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Period Economics</h2>
      </header>
      <div className="grid gap-px bg-[#EFE7DF] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {metrics.map((metric) => (
          <div key={metric.label} className="min-w-0 bg-white px-4 py-3 xl:px-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#7B6B72]">{metric.label}</p>
            <p className="mt-2 font-display text-2xl leading-none text-[#1F1A20] xl:text-xl">{loading ? '--' : metric.value}</p>
            <div className="mt-2">
              {loading ? (
                <p className="inline-flex rounded-full border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-1 text-[11px] font-medium leading-snug text-[#7B6B72]">
                  --
                </p>
              ) : metric.variance || metric.varianceMeta ? (
                <VariancePill value={variance} metadata={metric.varianceMeta} basis={metric.varianceBasis} />
              ) : (
                <p className="inline-flex rounded-full border border-[#C4D8CB] bg-[#EDF7EF] px-3 py-1 text-[11px] font-medium leading-snug text-[#5E8C67]">
                  {metric.helper}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FixedDepartmentBreakdown({ rows, tiled = false }: { rows: FixedDepartmentSummaryRow[]; tiled?: boolean }): React.ReactElement | null {
  if (rows.length === 0) return null;
  const maxBudget = Math.max(...rows.map((row) => Math.max(row.budget, row.spend)), 1);

  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Fixed Department Utilization</h2>
      </header>
      <div className={tiled ? 'grid gap-px bg-[#EFE7DF] md:grid-cols-2 2xl:grid-cols-3' : 'divide-y divide-[#EFE7DF]'}>
        {rows.map((row) => {
          const spendPct = Math.min(100, (row.spend / maxBudget) * 100);
          const budgetPct = Math.min(100, (row.budget / maxBudget) * 100);
          const utilization = row.budget > 0 ? (row.spend / row.budget) * 100 : 0;
          const budgetMissing = !row.budgetAvailable;
          return (
            <div key={row.label} className={tiled ? 'bg-white px-5 py-4' : 'px-5 py-4'}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#1F1A20]">{row.label}</p>
                  <p className="mt-1 text-xs text-[#6B5E62]">
                    {budgetMissing ? 'Budget unavailable' : `${formatPercent(utilization)} of budget used`}
                  </p>
                </div>
                <div className="text-right text-sm tabular-nums">
                  <p className="font-semibold text-[#1F1A20]">{formatCurrency(row.spend)}</p>
                  {budgetMissing ? (
                    <p className="mt-1 text-xs font-semibold text-[#6B5E62]">Utilization N/A</p>
                  ) : (
                    <div className={`mt-1 font-semibold ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                      <VarianceValue value={row.variance} metadata={row.varianceMeta} />
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                <div className="h-2 overflow-hidden rounded-full bg-[#F4EEE8]">
                  <div className="h-full rounded-full bg-[#6E3450]" style={{ width: `${spendPct}%` }} />
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#F4EEE8]">
                  <div className="h-full rounded-full bg-[#B69BA7]" style={{ width: `${budgetPct}%` }} />
                </div>
              </div>
              <div className="mt-2 flex justify-between text-[11px] font-medium text-[#8E8286]">
                <span>Spend {formatCurrency(row.spend)}</span>
                <span>{budgetMissing ? 'Budget unavailable' : `Budget ${formatCurrency(row.budget)}`}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function LineProductivityTable({ rows }: { rows: LineLaborChartRow[] }): React.ReactElement | null {
  if (rows.length === 0) return null;
  const sortedRows = [...rows].sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Line Productivity</h2>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#F7F1EA] text-xs font-semibold uppercase tracking-[0.08em] text-[#6B5E62]">
              <th className="px-4 py-3 text-left">Line</th>
              <th className="px-4 py-3 text-right">Spend</th>
              <th className="px-4 py-3 text-right">Earned</th>
              <th className="px-4 py-3 text-right">Variance (in $)</th>
              <th className="px-4 py-3 text-right">Variance (percentage)</th>
              <th className="px-4 py-3 text-right leading-tight"><MetricHeaderLabel label="Favourable/Unfavourable" /></th>
              <th className="px-4 py-3 text-right">Units</th>
              <th className="px-4 py-3 text-right">Spend / UOM</th>
              <th className="px-4 py-3 text-right">Spend / Earned Hr</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.lineName} className="border-t border-[#EFE7DF]">
                <td className="px-4 py-3 font-medium text-[#1F1A20]">{row.lineName}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.spend)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.earned)}</td>
                <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  {formatCurrency(row.variance)}
                </td>
                <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  {variancePctLabel(row.varianceMeta, row.variance, row.earned) ?? 'N/A'}
                </td>
                <td className={`px-4 py-3 text-right font-semibold ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  <VarianceStatusContent value={row.variance} metadata={row.varianceMeta} />
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {row.producedUnits === null ? 'N/A' : `${formatNumber(row.producedUnits)} ${row.unitOfMeasure}`}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {row.producedUnits !== null && row.spend !== null && row.producedUnits > 0
                    ? formatRate(row.spend / row.producedUnits)
                    : 'N/A'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {row.earnedHours !== null && row.spend !== null && row.earnedHours > 0
                    ? formatCurrency(row.spend / row.earnedHours)
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DailyExceptionTable({ rows }: { rows: DailyExceptionRow[] }): React.ReactElement | null {
  if (rows.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Daily Exception Review</h2>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#F7F1EA] text-xs font-semibold uppercase tracking-[0.08em] text-[#6B5E62]">
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-right">Spend</th>
              <th className="px-4 py-3 text-right">Budget + Earned</th>
              <th className="px-4 py-3 text-right">Variance (in $)</th>
              <th className="px-4 py-3 text-right">Variance (percentage)</th>
              <th className="px-4 py-3 text-right leading-tight"><MetricHeaderLabel label="Favourable/Unfavourable" /></th>
              <th className="px-4 py-3 text-right">Units</th>
              <th className="px-4 py-3 text-right">Spend / Unit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date} className="border-t border-[#EFE7DF]">
                <td className="px-4 py-3 font-medium text-[#1F1A20]">{row.date}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.spend)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.plannedEarned)}</td>
                <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  {formatCurrency(row.variance)}
                </td>
                <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  {variancePctLabel(row.varianceMeta, row.variance, row.plannedEarned) ?? 'N/A'}
                </td>
                <td className={`px-4 py-3 text-right font-semibold ${varianceTextClass(row.variance, row.varianceMeta)}`}>
                  <VarianceStatusContent value={row.variance} metadata={row.varianceMeta} />
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatNumber(row.units)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {row.units !== null && row.spend !== null && row.units > 0
                    ? formatRate(row.spend / row.units)
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LaborPerformanceDeepDive({
  comparableFixedBudget,
  comparableVariableEarned,
  comparableFixedSpend,
  comparableVariableSpend,
  netVariance,
  producedUnits,
  netVarianceMeta,
  departmentRows,
  lineRows,
  exceptionRows,
  loading = false,
}: {
  comparableFixedBudget: number;
  comparableVariableEarned: number;
  comparableFixedSpend: number;
  comparableVariableSpend: number;
  netVariance: number;
  producedUnits: number;
  netVarianceMeta?: VarianceMetadata | null;
  departmentRows: FixedDepartmentSummaryRow[];
  lineRows: LineLaborChartRow[];
  exceptionRows: DailyExceptionRow[];
  loading?: boolean;
}): React.ReactElement {
  const showDepartments = departmentRows.length > 0;
  const showLines = hasChartValues(lineRows, ['spend', 'earned', 'variance', 'producedUnits']);

  return (
    <div className="flex flex-col gap-4">
      <PerformanceMetricStrip
        fixedBudget={comparableFixedBudget}
        variableEarned={comparableVariableEarned}
        fixedSpend={comparableFixedSpend}
        variableSpend={comparableVariableSpend}
        netVariance={netVariance}
        producedUnits={producedUnits}
        netVarianceMeta={netVarianceMeta}
        loading={loading}
      />
      {!loading && showDepartments && showLines ? (
        <div className="grid gap-4 xl:grid-cols-[0.95fr_1.25fr]">
          <FixedDepartmentBreakdown rows={departmentRows} />
          <LineProductivityTable rows={lineRows} />
        </div>
      ) : null}
      {!loading && showDepartments && !showLines ? <FixedDepartmentBreakdown rows={departmentRows} tiled /> : null}
      {!loading && !showDepartments && showLines ? <LineProductivityTable rows={lineRows} /> : null}
      {!loading ? <DailyExceptionTable rows={exceptionRows} /> : null}
    </div>
  );
}

function LaborLineTotals({ report }: { report: LaborRecord | null }): React.ReactElement | null {
  const lineTotals = recordFrom(recordFrom(report?.labor_p3_report).line_total_rows);
  if (Object.keys(lineTotals).length === 0) return null;
  const rows = Object.entries(lineTotals).map(([lineName, payload]) => {
    const payloadRecord = recordFrom(payload);
    return {
      lineName,
      values: recordFrom(payloadRecord.values_by_cell),
      fields: recordFrom(payloadRecord.field_by_cell),
      derived: recordFrom(payloadRecord.derived_values),
      varianceMeta: varianceMetadataFrom(recordFrom(payloadRecord.variance_metadata).variance),
    };
  });
  if (rows.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-md border border-[#EDE6E1] bg-white shadow-sm">
      <header className="border-b border-[#EDE5D8] px-5 py-3">
        <h2 className="text-sm font-semibold text-[#1F1A20]">Line Detail Totals</h2>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] border-collapse text-sm">
          <thead>
            <tr className="bg-[#F7F1EA] text-left text-xs font-semibold uppercase tracking-[0.08em] text-[#6B5E62]">
              <th className="px-4 py-3">Line</th>
              <th className="px-4 py-3 text-right">Employee</th>
              <th className="px-4 py-3 text-right">Benefits</th>
              <th className="px-4 py-3 text-right">Temps</th>
              <th className="px-4 py-3 text-right">Earned</th>
              <th className="px-4 py-3 text-right">Variance (in $)</th>
              <th className="px-4 py-3 text-right">Variance (percentage)</th>
              <th className="px-4 py-3 text-right leading-tight"><MetricHeaderLabel label="Favourable/Unfavourable" /></th>
              <th className="px-4 py-3 text-right">Produced</th>
              <th className="px-4 py-3 text-right">Earned Hours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ lineName, values, fields, derived, varianceMeta }) => {
              const valueFor = (fieldName: string) => lineTotalValue(values, fields, fieldName);
              const employeeSpendValue = valueFor('employee_spend');
              const tempSpendValue = valueFor('temp_spend');
              const earnedValue = valueFor('earned');
              const employeeSpend = isUnavailableValue(employeeSpendValue) ? null : toNumber(employeeSpendValue);
              const benefits = toNumber(valueFor('benefits'));
              const tempSpend = isUnavailableValue(tempSpendValue) ? null : toNumber(tempSpendValue);
              const earned = isUnavailableValue(earnedValue) ? null : toNumber(earnedValue);
              const variance = isUnavailableValue(derived.variance) ? null : toNumber(derived.variance);
              return (
                <tr key={lineName} className="border-t border-[#EFE7DF]">
                  <td className="px-4 py-3 font-medium text-[#1F1A20]">{lineName}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(employeeSpend)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(benefits)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(tempSpend)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(earned)}</td>
                  <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(variance, varianceMeta)}`}>
                    {formatCurrency(variance)}
                  </td>
                  <td className={`px-4 py-3 text-right font-semibold tabular-nums ${varianceTextClass(variance, varianceMeta)}`}>
                    {variancePctLabel(varianceMeta, variance, earned) ?? 'N/A'}
                  </td>
                  <td className={`px-4 py-3 text-right font-semibold ${varianceTextClass(variance, varianceMeta)}`}>
                    <VarianceStatusContent value={variance} metadata={varianceMeta} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatNumber(valueFor('units_produced'))}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatDecimal(valueFor('earned_hours'))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function LaborPerformancePage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSelectionRef = useRef<InitialLaborSelection | null>(null);
  if (!initialSelectionRef.current) {
    initialSelectionRef.current = readInitialLaborSelection(searchParams);
  }
  const initialSelection = initialSelectionRef.current;
  const [calculationReport, setCalculationReport] = useState<LaborRecord | null>(null);
  const [view, setView] = useState<LaborReportView>('dashboard');
  const [gridSection, setGridSection] = useState<LaborGridSection>('fixed');
  const [variableGridDetailSections, setVariableGridDetailSections] = useState<string[]>(['all']);
  const [loading, setLoading] = useState(true);
  const [loadingSavedReport, setLoadingSavedReport] = useState(false);
  const [loadingPlants, setLoadingPlants] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeRefreshJob, setActiveRefreshJob] = useState<LaborP3RefreshJobResponse | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshCompletedAt, setRefreshCompletedAt] = useState<string | null>(null);
  const [refreshMetadata, setRefreshMetadata] = useState<LaborRecord | null>(null);
  const [periodStoryNarrative, setPeriodStoryNarrative] = useState<{ main: string; details: string; recommendations?: string; context?: string; word_counts?: LaborRecord } | null>(null);
  const [periodStoryLoading, setPeriodStoryLoading] = useState(false);
  const [periodStoryError, setPeriodStoryError] = useState<string | null>(null);
  const [plants, setPlants] = useState<LaborPlantOption[]>([
    { plant: DEFAULT_PLANT, plant_name: 'Carlstadt', source: 'default' },
  ]);
  const [selectedPlant, setSelectedPlant] = useState(initialSelection.plant ?? DEFAULT_PLANT);
  const [fiscalPeriod, setFiscalPeriod] = useState(initialSelection.fiscalPeriod ?? DEFAULT_FISCAL_YEAR_PERIOD);
  const [error, setError] = useState<string | null>(null);
  const [downloadingGrid, setDownloadingGrid] = useState(false);
  const [downloadingReconciliationPackage, setDownloadingReconciliationPackage] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSentiment, setFeedbackSentiment] = useState<'good' | 'bad'>('good');
  const [feedbackCategory, setFeedbackCategory] = useState(feedbackCategoryOptions[0]);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [contentRef, measuredContentWidth] = useElementWidth<HTMLDivElement>();
  const errorRef = useRef<HTMLElement | null>(null);
  const refreshAbortRef = useRef<AbortController | null>(null);
  const selectedContextRef = useRef({ plant: selectedPlant, fiscalPeriod });
  const contentWidth = measuredContentWidth || 1440;
  const { success: toastSuccess, error: toastError } = useGlobalToast();

  useEffect(() => {
    selectedContextRef.current = { plant: selectedPlant, fiscalPeriod };
  }, [fiscalPeriod, selectedPlant]);

  useEffect(() => {
    persistLaborSelection(selectedPlant, fiscalPeriod);
    setSearchParams((current) => {
      if (current.get('sap_exchange') || current.get('sap_error')) return current;
      const next = new URLSearchParams(current);
      if (
        next.get(LABOR_PLANT_QUERY_PARAM) === selectedPlant &&
        next.get(LABOR_PERIOD_QUERY_PARAM) === fiscalPeriod
      ) {
        return current;
      }
      next.set(LABOR_PLANT_QUERY_PARAM, selectedPlant);
      next.set(LABOR_PERIOD_QUERY_PARAM, fiscalPeriod);
      next.delete('period');
      return next;
    }, { replace: true });
  }, [fiscalPeriod, selectedPlant, setSearchParams]);

  const clearSapCallbackParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('sap_exchange');
    next.delete('sap_error');
    next.delete('message');
    setSearchParams(next, { replace: true });
  };

  const beginSapReconnect = async (plant: string, period: string, cause?: string | null) => {
    try {
      window.sessionStorage.setItem(
        PENDING_LABOR_SAP_REFRESH_KEY,
        JSON.stringify({ plant, fiscalPeriod: period, cause: cause ?? null }),
      );
    } catch {
      // If storage is unavailable, OAuth can still reconnect; the user may need
      // to click Refresh again after returning.
    }
    setRefreshError(SAP_RECONNECT_MESSAGE);
    try {
      await sapOAuthService.initiateConnection('labor-performance');
    } catch (err) {
      try {
        window.sessionStorage.removeItem(PENDING_LABOR_SAP_REFRESH_KEY);
      } catch {
        // No-op.
      }
      setRefreshError(getErrorMessage(err, formatLaborPeriodLabel(period)));
      throw err;
    }
  };

  const loadReport = async (
    plant = selectedPlant,
    period = fiscalPeriod,
    options: { showToast?: boolean } = {},
  ) => {
    setLoading(true);
    setLoadingSavedReport(true);
    setError(null);
    setRefreshError(null);
    setRefreshCompletedAt(null);
    try {
      const report = await laborPerformanceApi.calculate({
        plant,
        fiscal_year_period: period,
        display_weeks: 5,
        validate_against_workbook: false,
      });
      setCalculationReport(report);
      setRefreshMetadata(recordFrom(report.report_metadata));
      if (options.showToast) {
        const periodLabel = formatLaborPeriodLabel(period);
        toastSuccess(
          `${periodLabel} report loaded`,
          `Last saved Labor Performance report for ${periodLabel} has loaded.`,
        );
      }
    } catch (err) {
      setCalculationReport(null);
      setRefreshMetadata(null);
      setError(getErrorMessage(err, formatLaborPeriodLabel(period)));
    } finally {
      setLoading(false);
      setLoadingSavedReport(false);
    }
  };

  const applyRefreshResult = (refreshed: LaborP3RefreshResponse) => {
    if (refreshed.calculation) {
      setCalculationReport(refreshed.calculation);
      const metadata = recordFrom(refreshed.refresh_metadata);
      const reportMetadata = recordFrom(refreshed.calculation.report_metadata);
      setRefreshMetadata({ ...reportMetadata, ...metadata });
      setRefreshCompletedAt(stringValue(metadata.refreshed_at) ?? new Date().toISOString());
      setLoading(false);
    } else {
      setRefreshError(`Refresh completed, but no calculated Labor ${formatLaborPeriodLabel(fiscalPeriod)} report was returned.`);
    }
  };

  const pollRefreshJob = async (
    runId: string,
    controller: AbortController,
    jobPlant = selectedPlant,
    jobPeriod = fiscalPeriod,
  ) => {
    const refreshed = await laborPerformanceApi.waitForRefreshJob(runId, {
      signal: controller.signal,
      onStatus: (latest) => {
        setActiveRefreshJob(latest);
        setRefreshCompletedAt(latest.completed_at ?? null);
      },
    });
    setActiveRefreshJob(null);
    const current = selectedContextRef.current;
    if (current.plant === jobPlant && current.fiscalPeriod === jobPeriod) {
      applyRefreshResult(refreshed);
    } else {
      setRefreshCompletedAt(new Date().toISOString());
    }
  };

  const refreshReport = async (plant = selectedPlant, period = fiscalPeriod) => {
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    setRefreshing(true);
    setError(null);
    setRefreshError(null);
    setRefreshCompletedAt(null);
    try {
      const job = await laborPerformanceApi.createSapRefreshJob({
        plant,
        fiscal_year_period: period,
        display_weeks: 5,
        replace_existing: true,
        generate_total_prod: true,
        calculate: true,
      });
      setActiveRefreshJob(job);
      const jobPlant = job.plant ?? plant;
      const jobPeriod = job.fiscal_year_period ?? period;
      if (jobPlant === plant && jobPeriod === period) {
        setCalculationReport(null);
        setRefreshMetadata(null);
      }
      if (String(job.status ?? '').toUpperCase() === 'COMPLETED' && job.result) {
        setActiveRefreshJob(null);
        applyRefreshResult(job.result);
        const periodLabel = formatLaborPeriodLabel(jobPeriod);
        const plantLabel = `Plant ${jobPlant}`;
        toastSuccess(
          `${periodLabel} ${plantLabel} refresh complete`,
          `The ${periodLabel} ${plantLabel} dashboard has been updated with the latest completed refresh.`,
        );
      } else {
        await pollRefreshJob(job.run_id, controller, jobPlant, jobPeriod);
        if (selectedContextRef.current.plant === jobPlant && selectedContextRef.current.fiscalPeriod === jobPeriod) {
          const periodLabel = formatLaborPeriodLabel(jobPeriod);
          const plantLabel = `Plant ${jobPlant}`;
          toastSuccess(
            `${periodLabel} ${plantLabel} refresh complete`,
            `The ${periodLabel} ${plantLabel} dashboard has been updated with the latest completed refresh.`,
          );
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (isLaborSapReconnectError(err)) {
        await beginSapReconnect(plant, period, getLaborSapReconnectErrorCode(err));
        return;
      }
      setRefreshError(getErrorMessage(err, formatLaborPeriodLabel(period)) || REFRESH_FAILED_MESSAGE);
    } finally {
      if (refreshAbortRef.current === controller) {
        refreshAbortRef.current = null;
        setRefreshing(false);
      }
    }
  };

  const resumeLatestRefresh = async (plant: string, period = fiscalPeriod): Promise<boolean> => {
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    let startedBackgroundPoll = false;
    try {
      const latestJob = await laborPerformanceApi.getActiveRefreshJob();
      if (!latestJob) {
        setActiveRefreshJob(null);
        if (refreshAbortRef.current === controller) refreshAbortRef.current = null;
        return false;
      }
      const jobPlant = latestJob.plant ?? plant;
      const jobPeriod = latestJob.fiscal_year_period ?? period;
      setActiveRefreshJob(latestJob);
      setRefreshError(null);
      if (String(latestJob.status ?? '').toUpperCase() === 'COMPLETED' && latestJob.result) {
        setActiveRefreshJob(null);
        if (selectedContextRef.current.plant === jobPlant && selectedContextRef.current.fiscalPeriod === jobPeriod) {
          applyRefreshResult(latestJob.result);
        }
        setRefreshCompletedAt(latestJob.completed_at ?? stringValue(latestJob.result.refresh_metadata?.refreshed_at));
        if (refreshAbortRef.current === controller) refreshAbortRef.current = null;
        return true;
      }
      setRefreshing(true);
      startedBackgroundPoll = true;
      setRefreshCompletedAt(latestJob.created_at ?? null);
      setLoading(false);
      setLoadingSavedReport(false);
      void pollRefreshJob(latestJob.run_id, controller, jobPlant, jobPeriod)
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setRefreshError(getErrorMessage(err, formatLaborPeriodLabel(jobPeriod)) || REFRESH_FAILED_MESSAGE);
        })
        .finally(() => {
          if (refreshAbortRef.current === controller) {
            refreshAbortRef.current = null;
            setRefreshing(false);
            setActiveRefreshJob(null);
          }
        });
      return true;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return true;
      if (isLaborSapReconnectError(err)) {
        await beginSapReconnect(plant, period, getLaborSapReconnectErrorCode(err));
        return true;
      }
      setRefreshError(getErrorMessage(err, formatLaborPeriodLabel(period)) || REFRESH_FAILED_MESSAGE);
      return false;
    } finally {
      if (!startedBackgroundPoll && refreshAbortRef.current === controller) {
        refreshAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!loading && error) {
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [error, loading]);

  useEffect(() => {
    const sapExchange = searchParams.get('sap_exchange');
    const sapError = searchParams.get('sap_error');
    if (!sapExchange && !sapError) return;

    if (sapError) {
      try {
        window.sessionStorage.removeItem(PENDING_LABOR_SAP_REFRESH_KEY);
      } catch {
        // No-op.
      }
      setRefreshError(searchParams.get('message') || 'SAP Datasphere connection failed. Please try again.');
      clearSapCallbackParams();
      return;
    }

    let pending: { plant?: string; fiscalPeriod?: string } | null = null;
    try {
      const raw = window.sessionStorage.getItem(PENDING_LABOR_SAP_REFRESH_KEY);
      pending = raw ? JSON.parse(raw) : null;
      window.sessionStorage.removeItem(PENDING_LABOR_SAP_REFRESH_KEY);
    } catch {
      pending = null;
    }

    clearSapCallbackParams();
    if (!pending) {
      setRefreshError(null);
      return;
    }

    const plant = pending.plant || selectedPlant;
    const period = pending.fiscalPeriod || fiscalPeriod;
    setSelectedPlant(plant);
    setFiscalPeriod(period);
    void refreshReport(plant, period);
  // Reacting only to the callback query params keeps the OAuth retry one-shot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    const loadPlants = async () => {
      setLoadingPlants(true);
      setLoading(true);
      let reportPlant = selectedPlant;
      const reportPeriod = fiscalPeriod;
      try {
        const response = await laborPerformanceApi.listPlants();
        if (!active) return;
        const normalizedPlants = normalizePlantOptions(response.rows);
        if (normalizedPlants.length > 0) {
          setPlants(normalizedPlants);
          reportPlant = normalizedPlants.some((plant) => plant.plant === selectedPlant)
            ? selectedPlant
            : normalizedPlants[0].plant;
          setSelectedPlant(reportPlant);
        }
      } catch {
        // Carlstadt is the only enabled plant for now; keep the default option
        // if the optional plant-list request is unavailable.
      } finally {
        if (active) setLoadingPlants(false);
      }
      if (active) {
        await resumeLatestRefresh(reportPlant, reportPeriod);
        if (active) {
          await loadReport(reportPlant, reportPeriod);
        }
      }
    };
    void loadPlants();
    return () => {
      active = false;
      refreshAbortRef.current?.abort();
    };
  // Initial loading must run once; subsequent plant/period changes are explicit user actions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fixedRows = rowsFrom(recordFrom(calculationReport?.fixed_labor).rows);
  const variableRows = rowsFrom(recordFrom(calculationReport?.variable_labor).rows);
  const dashboardSummary = recordFrom(calculationReport?.dashboard_summary);
  const dashboardKpis = recordFrom(dashboardSummary.kpis);
  const dashboardPeriodEconomics = recordFrom(dashboardSummary.period_economics);
  const variableEarnedCalculated = sumBy(variableRows, 'total_variable_earned');
  const fixedSpend = sumBy(fixedRows, 'total_fixed_spent');
  const fixedBudget = sumBy(fixedRows, 'total_fixed_budget');
  const variableSpend = sumBy(variableRows, 'total_variable_spent');
  const hasFixedVariance = (row: LaborRecord) => !isUnavailableValue(row.total_fixed_variance);
  const hasVariableVariance = (row: LaborRecord) => !isUnavailableValue(row.total_variable_variance);
  const comparableFixedBudgetCalculated = sumByWhen(fixedRows, 'total_fixed_budget', hasFixedVariance);
  const comparableFixedSpendCalculated = sumByWhen(fixedRows, 'total_fixed_spent', hasFixedVariance);
  const comparableVariableEarnedCalculated = sumByWhen(variableRows, 'total_variable_earned', hasVariableVariance);
  const comparableVariableSpendCalculated = sumByWhen(variableRows, 'total_variable_spent', hasVariableVariance);
  const fixedVarianceCalculated = sumBy(fixedRows, 'total_fixed_variance');
  const variableVarianceCalculated = sumBy(variableRows, 'total_variable_variance');
  const producedUnitsCalculated = sumBy(variableRows, 'units');
  const fixedVariance = isUnavailableValue(dashboardKpis.fixed_labor_variance) ? fixedVarianceCalculated : toNumber(dashboardKpis.fixed_labor_variance);
  const variableVariance = isUnavailableValue(dashboardKpis.variable_labor_variance) ? variableVarianceCalculated : toNumber(dashboardKpis.variable_labor_variance);
  const variableEarned = isUnavailableValue(dashboardKpis.earned_labor) ? variableEarnedCalculated : toNumber(dashboardKpis.earned_labor);
  const laborSpend = isUnavailableValue(dashboardKpis.labor_spend) ? fixedSpend + variableSpend : toNumber(dashboardKpis.labor_spend);
  const producedUnits = isUnavailableValue(dashboardKpis.produced_units) ? producedUnitsCalculated : toNumber(dashboardKpis.produced_units);
  const activeProductionDaysCalculated = variableRows.reduce((total, row) => total + (toNumber(row.active_flag) > 0 ? 1 : 0), 0);
  const activeProductionDays = isUnavailableValue(dashboardKpis.active_production_days)
    ? activeProductionDaysCalculated
    : toNumber(dashboardKpis.active_production_days);
  const netLaborVariance = isUnavailableValue(dashboardPeriodEconomics.net_labor_variance)
    ? fixedVariance + variableVariance
    : toNumber(dashboardPeriodEconomics.net_labor_variance);
  const comparableFixedBudget = comparableFixedBudgetCalculated;
  const comparableFixedSpend = comparableFixedSpendCalculated;
  const comparableVariableEarned = comparableVariableEarnedCalculated;
  const comparableVariableSpend = comparableVariableSpendCalculated;
  const rollupVarianceMetadata = recordFrom(recordFrom(calculationReport?.labor_p3_report).variance_metadata);
  const netVarianceMeta = varianceMetadataFrom(rollupVarianceMetadata.net_labor_variance);
  const reportMetadata = recordFrom(calculationReport?.report_metadata);
  const statusMetadata = refreshMetadata ?? reportMetadata;
  const actualsStart = stringValue(statusMetadata.workday_date_from) ?? stringValue(reportMetadata.workday_date_from);
  const actualsEnd = stringValue(statusMetadata.workday_date_to) ?? stringValue(reportMetadata.workday_date_to);
  const selectedPlantOption = plants.find((plant) => plant.plant === selectedPlant);
  const selectedPlantLabel = selectedPlantOption ? getPlantLabel(selectedPlantOption) : selectedPlant;
  const plantName = typeof calculationReport?.plant_name === 'string' ? calculationReport.plant_name : selectedPlantOption?.plant_name || selectedPlant;
  const plantCode = typeof calculationReport?.plant === 'string' ? calculationReport.plant : selectedPlant;
  const reportPeriod = typeof calculationReport?.fiscal_year_period === 'string' ? calculationReport.fiscal_year_period : fiscalPeriod;
  const reportPeriodLabel = formatLaborPeriodLabel(reportPeriod);
  const dataQualityMessages = useMemo(
    () => buildLaborDataQualityMessages(reportMetadata, reportPeriodLabel),
    [reportMetadata, reportPeriodLabel],
  );
  const kpiMetrics = useMemo<LaborKpiCardData[]>(() => [
    {
      id: 'active-production-days',
      label: 'Active Prod Days',
      value: activeProductionDays,
      format: 'number',
      helper: 'from workbook active flag',
    },
    {
      id: 'fixed-labor-variance',
      label: 'Fixed Labor Variance',
      value: fixedVariance,
      format: 'currency',
      helper: 'vs fixed labor budget',
      variance: true,
      varianceBasis: comparableFixedBudget,
      varianceMeta: varianceMetadataFrom(rollupVarianceMetadata.fixed_labor_variance),
    },
    {
      id: 'variable-labor-variance',
      label: 'Variable Labor Variance',
      value: variableVariance,
      format: 'currency',
      helper: 'earned labor less spend',
      variance: true,
      varianceBasis: comparableVariableEarned,
      varianceMeta: varianceMetadataFrom(rollupVarianceMetadata.variable_labor_variance),
    },
    {
      id: 'earned-labor',
      label: 'Earned Labor',
      value: variableEarned,
      format: 'currency',
      helper: 'from production rates',
    },
    {
      id: 'labor-spend',
      label: 'Total Labor Spend',
      value: laborSpend,
      format: 'currency',
      helper: 'fixed plus variable',
    },
    {
      id: 'cases-units-produced',
      label: 'Cases / Units Produced',
      value: producedUnits,
      format: 'number',
      helper: 'variable production units',
    },
  ], [activeProductionDays, comparableFixedBudget, comparableVariableEarned, fixedVariance, laborSpend, producedUnits, rollupVarianceMetadata.fixed_labor_variance, rollupVarianceMetadata.variable_labor_variance, variableEarned, variableVariance]);
  const detailViews = useMemo(() => ({
    fixed: buildFixedDetailView(calculationReport),
    variable: buildVariableDetailView(calculationReport),
  }), [calculationReport]);
  const variableGridSectionOptions = useMemo(() => buildDetailSectionOptions(detailViews.variable), [detailViews.variable]);
  const filteredVariableDetailView = useMemo(
    () => filterDetailView(detailViews.variable, 'lines', variableGridDetailSections),
    [detailViews.variable, variableGridDetailSections],
  );
  const gridCsvSections = useMemo(
    () => buildLaborGridCsvSections({
      gridSection,
      report: calculationReport,
      fixedRows,
      variableRows,
      fixedDetailView: detailViews.fixed,
      variableDetailView: filteredVariableDetailView,
    }),
    [calculationReport, detailViews.fixed, filteredVariableDetailView, fixedRows, gridSection, variableRows],
  );
  const dailyChartRows = useMemo(
    () => buildDailyLaborChartRows(calculationReport, fixedRows, variableRows).filter((row) => rowDateInRange(row, actualsStart, actualsEnd)),
    [actualsEnd, actualsStart, calculationReport, fixedRows, variableRows],
  );
  const lineChartRows = useMemo(
    () => {
      const summaryRows = dashboardLineRowsFrom(dashboardSummary.line_productivity);
      return summaryRows.length > 0 ? summaryRows : buildLineLaborChartRows(calculationReport, actualsStart, actualsEnd);
    },
    [actualsEnd, actualsStart, calculationReport, dashboardSummary.line_productivity],
  );
  const fixedDepartmentRows = useMemo(
    () => {
      const summaryRows = dashboardDepartmentRowsFrom(dashboardSummary.fixed_department_utilization);
      return summaryRows.length > 0 ? summaryRows : buildFixedDepartmentSummaryRows(fixedRows, calculationReport);
    },
    [calculationReport, dashboardSummary.fixed_department_utilization, fixedRows],
  );
  const dailyExceptionRows = useMemo(
    () => {
      const summaryRows = dashboardExceptionRowsFrom(dashboardSummary.daily_exceptions);
      return summaryRows.length > 0 ? summaryRows : buildDailyExceptionRows(dailyChartRows);
    },
    [dailyChartRows, dashboardSummary.daily_exceptions],
  );
  useEffect(() => {
    if (!calculationReport) {
      setPeriodStoryNarrative(null);
      setPeriodStoryError(null);
      setPeriodStoryLoading(false);
      return;
    }

    const narrative = recordFrom(calculationReport.period_story_narrative);
    const main = typeof narrative.main === 'string' ? narrative.main.trim() : '';
    const details = typeof narrative.details === 'string' ? narrative.details.trim() : '';
    const recommendations = typeof narrative.recommendations === 'string' ? narrative.recommendations.trim() : '';
    const context = typeof narrative.context === 'string' ? narrative.context.trim() : '';
    const wordCounts = recordFrom(narrative.word_counts);
    setPeriodStoryLoading(false);
    setPeriodStoryError(null);
    if (main && details) {
      setPeriodStoryNarrative({
        main,
        details,
        recommendations,
        context,
        word_counts: wordCounts,
      });
    } else {
      setPeriodStoryNarrative(null);
      setPeriodStoryError('AI story is unavailable.');
    }
  }, [calculationReport]);
  const showFixedGrid = gridSection === 'fixed' || gridSection === 'all';
  const showVariableGrid = gridSection === 'variable' || gridSection === 'all';
  const hasRenderableContent = !!calculationReport;
  const reportControlsDisabled = loadingSavedReport;
  const reportActionButtonsDisabled = loading || loadingSavedReport;
  const showReportViewToggle = hasRenderableContent && !loading && !reportControlsDisabled && !error;
  const statusError = refreshError || error;
  const loadingLastReport = loadingSavedReport;
  const activeRefreshResult = recordFrom(activeRefreshJob?.result);
  const activeRefreshResultCalculation = recordFrom(activeRefreshResult.calculation);
  const activeRefreshResultMetadata = recordFrom(activeRefreshResult.refresh_metadata);
  const activeRefreshResultSourceFiles = recordFrom(activeRefreshResultMetadata.source_files);
  const activeRefreshPeriod = stringValue(activeRefreshJob?.fiscal_year_period)
    ?? stringValue(activeRefreshResultCalculation.fiscal_year_period)
    ?? stringValue(activeRefreshResultMetadata.fiscal_year_period)
    ?? stringValue(activeRefreshResultSourceFiles.fiscal_year_period);
  const activeRefreshPlant = stringValue(activeRefreshJob?.plant)
    ?? stringValue(activeRefreshResultCalculation.plant)
    ?? stringValue(activeRefreshResultMetadata.plant);
  const activeRefreshPeriodLabel = activeRefreshPeriod
    ? formatLaborPeriodLabel(activeRefreshPeriod)
    : 'Labor Performance';
  const activeRefreshPlantLabel = activeRefreshPlant ? `Plant ${activeRefreshPlant}` : 'selected plant';
  const activeRefreshSubjectLabel = activeRefreshPeriod
    ? `${activeRefreshPeriodLabel} ${activeRefreshPlantLabel}`
    : activeRefreshPlant
      ? activeRefreshPlantLabel
      : 'Labor Performance';
  const selectedPeriodIsRefreshing = Boolean(
    activeRefreshJob &&
    (activeRefreshPlant ?? selectedPlant) === selectedPlant &&
    normalizeFiscalPeriod(activeRefreshPeriod) === normalizeFiscalPeriod(fiscalPeriod),
  );
  const reconciliationDownloadDisabled = reportActionButtonsDisabled || selectedPeriodIsRefreshing;
  const selectedLoadingPeriodLabel = formatLaborPeriodLabel(fiscalPeriod);
  const selectedStatusPlantLabel = selectedPlant ? `Plant ${selectedPlant}` : 'selected plant';
  const statusTitle = loadingLastReport
    ? `Loading ${selectedLoadingPeriodLabel} last refreshed report`
    : refreshing
      ? 'Refresh data is running in the background'
      : refreshError
        ? 'Refresh data could not complete'
        : error
          ? 'Could not load the report'
          : 'Refresh complete';
  const statusMessage = loadingLastReport
    ? `Loading the last saved ${selectedLoadingPeriodLabel} ${selectedStatusPlantLabel} Labor Performance report. No SAP data is being refreshed for this period.`
    : refreshing
      ? selectedPeriodIsRefreshing
        ? `${activeRefreshSubjectLabel} refresh is running. View is paused for this period until refresh completes, and new refreshes are paused to reduce SAP load.`
        : `${activeRefreshSubjectLabel} refresh is running. New refreshes are paused to reduce SAP load, but other saved reports can still be viewed.`
      : statusError || `The ${selectedLoadingPeriodLabel} ${selectedStatusPlantLabel} dashboard has been updated with the latest completed refresh.`;
  const periodStart = stringValue(statusMetadata.period_start) ?? stringValue(reportMetadata.period_start) ?? stringValue(recordFrom(calculationReport?.fixed_labor).period_start);
  const periodEnd = stringValue(statusMetadata.period_end) ?? stringValue(reportMetadata.period_end) ?? stringValue(recordFrom(calculationReport?.fixed_labor).period_end);
  const displayStart = stringValue(statusMetadata.display_start) ?? stringValue(reportMetadata.display_start) ?? stringValue(recordFrom(calculationReport?.fixed_labor).display_start);
  const displayEnd = stringValue(statusMetadata.display_end) ?? stringValue(reportMetadata.display_end) ?? stringValue(recordFrom(calculationReport?.fixed_labor).display_end);
  const refreshedAt = stringValue(statusMetadata.refreshed_at) ?? refreshCompletedAt;
  const periodRangeLabel = periodStart && periodEnd
    ? `${formatFullDateLabel(periodStart)} - ${formatFullDateLabel(periodEnd)}`
    : 'Not available';
  const actualsRangeLabel = actualsStart && actualsEnd
    ? `${formatFullDateLabel(actualsStart)} - ${formatFullDateLabel(actualsEnd)}`
    : 'Not available';
  const canDownloadGrid = gridCsvSections.length > 0;
  const handleDownloadGrid = async () => {
    if (!canDownloadGrid || downloadingGrid) return;
    const filename = [
      `labor-${reportPeriodLabel.toLowerCase()}-grid`,
      sanitizeFilenamePart(plantCode),
      sanitizeFilenamePart(reportPeriod),
      gridSection,
    ].join('-') + '.xlsx';
    setDownloadingGrid(true);
    try {
      await downloadExcel(filename, gridCsvSections);
    } finally {
      setDownloadingGrid(false);
    }
  };
  const handleDownloadReconciliationPackage = async () => {
    if (downloadingReconciliationPackage || reconciliationDownloadDisabled) return;
    setDownloadingReconciliationPackage(true);
    try {
      await laborPerformanceApi.downloadReconciliationPackage({
        plant: selectedPlant,
        fiscal_year_period: fiscalPeriod,
      });
      toastSuccess(
        'Reconciliation package downloaded',
        `The ${formatLaborPeriodLabel(fiscalPeriod)} ${selectedPlantLabel} reconciliation files are ready for review.`,
      );
    } catch (error) {
      toastError(
        'Download failed',
        error instanceof LaborReconciliationDownloadError
          ? error.message
          : "We couldn't download the reconciliation package. Please run Refresh Data and try again.",
      );
    } finally {
      setDownloadingReconciliationPackage(false);
    }
  };
  const handleSubmitFeedback = async () => {
    const comment = feedbackComment.trim();
    if (!comment) {
      setFeedbackError('Please enter feedback before submitting.');
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await laborPerformanceApi.submitFeedback({
        plant: selectedPlant,
        fiscal_year_period: fiscalPeriod,
        view,
        category: `${feedbackSentiment === 'good' ? 'Good' : 'Bad'} - ${feedbackCategory}`,
        comment,
        context: {
          sentiment: feedbackSentiment,
          report_period_label: reportPeriodLabel,
          grid_section: gridSection,
          variable_grid_sections: variableGridDetailSections,
          has_report: hasRenderableContent,
          loading_last_report: loadingLastReport,
          refreshing,
          selected_period_is_refreshing: selectedPeriodIsRefreshing,
          active_refresh_job: activeRefreshJob
            ? {
              run_id: activeRefreshJob.run_id,
              status: activeRefreshJob.status,
              plant: activeRefreshJob.plant,
              fiscal_year_period: activeRefreshJob.fiscal_year_period,
            }
            : null,
          status_error: statusError,
          data_quality_note_ids: dataQualityMessages.map((message) => message.id),
        },
      });
      setFeedbackComment('');
      setFeedbackOpen(false);
      toastSuccess('Thanks, feedback saved', 'We captured your Labor Performance feedback.');
    } catch {
      setFeedbackError("We couldn't save the feedback. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <main className="h-full min-h-0 overflow-y-auto bg-[#FAF7F1] px-3 pb-20 pt-5 text-[#1F1A20] [scrollbar-gutter:stable] sm:px-4 lg:px-6 2xl:px-8">
      <div ref={contentRef} className="mx-auto flex w-full max-w-none flex-col gap-5">
        <header className="flex flex-col gap-3 border-b border-[#E6DED6] pb-4 lg:grid lg:grid-cols-4 lg:items-end">
          <div className="min-w-0 lg:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#6B5E62]">Smart Morning</p>
            <h1 className="mt-1 font-display text-3xl font-normal leading-tight text-[#1F1A20]">
              Labor {reportPeriodLabel} Performance
            </h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[#5E5256]">
              Pre-canned daily labor performance for {plantName} plant {plantCode}.
            </p>
          </div>
          <div className="flex w-full flex-wrap items-end gap-2 lg:col-span-3 lg:justify-between">
            <div className="flex w-full flex-wrap items-end gap-5 lg:ml-28 lg:flex-1 lg:justify-start xl:w-auto">
              <div className="relative flex w-full min-w-[10.5rem] flex-col gap-1 text-xs font-medium text-[#5E5256] sm:w-auto">
                <span>Plant</span>
                <div className="flex h-10 w-full items-center rounded-md border border-[#D9D0C8] bg-white px-3 text-left text-sm font-semibold text-[#2F2A2D] shadow-sm">
                  <span className="truncate">{loadingPlants ? 'Loading plants...' : selectedPlantLabel}</span>
                </div>
              </div>
              <label className="flex w-full min-w-[8rem] flex-col gap-1 text-xs font-medium text-[#5E5256] sm:w-32">
                Fiscal Period
                <input
                  value={fiscalPeriod}
                  onChange={(event) => setFiscalPeriod(event.target.value)}
                  disabled={reportControlsDisabled}
                  placeholder="003/2027"
                  className="h-10 w-full rounded-md border border-[#D9D0C8] bg-white px-3 text-sm font-semibold text-[#2F2A2D] shadow-sm outline-none transition focus:border-[#6E3450] disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>
              <button
                type="button"
                onClick={() => void loadReport(selectedPlant, fiscalPeriod, { showToast: true })}
                disabled={loading || loadingSavedReport || reportControlsDisabled || selectedPeriodIsRefreshing}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-[#6E3450] bg-white px-3 text-sm font-semibold text-[#6E3450] shadow-sm transition hover:bg-[#FAF7F1] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {loadingSavedReport ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                View Last Refreshed Report
              </button>
              <button
                type="button"
                onClick={() => void refreshReport()}
                disabled={loading || refreshing || loadingSavedReport}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#6E3450] px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#5A2A41] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
	              >
	                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
	                Refresh Data
	              </button>
            </div>
            <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:ml-8 sm:w-auto lg:ml-0">
              <button
                type="button"
                onClick={() => void handleDownloadReconciliationPackage()}
                disabled={downloadingReconciliationPackage || reconciliationDownloadDisabled}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-[#D9D0C8] bg-white px-3 text-sm font-semibold text-[#2F2A2D] shadow-sm transition hover:bg-[#F5EFE8] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {downloadingReconciliationPackage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Data Reconciliation
              </button>
              <button
                type="button"
                onClick={() => {
                  if (reportActionButtonsDisabled) return;
                  setFeedbackError(null);
                  setFeedbackOpen(true);
                }}
                disabled={reportActionButtonsDisabled}
                aria-label="Send feedback"
                title="Send feedback"
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-[#6E3450] bg-white px-3 text-sm font-semibold text-[#6E3450] shadow-sm transition hover:bg-[#FAF7F1] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <MessageSquare className="h-4 w-4" />
                Feedback
              </button>
            </div>
          </div>
        </header>

        {!reportControlsDisabled ? (
          <section className="grid gap-3 rounded-md border border-[#D9D0C8] bg-white px-5 py-3 text-sm text-[#4D4649] shadow-sm md:grid-cols-4 md:items-end md:px-8">
              <div className="md:justify-self-start">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A6C70]">Last Refreshed</p>
                <p className="mt-1 font-semibold text-[#1F1A20]">
                  {refreshedAt ? formatDateTimeLabel(refreshedAt) : 'Not available'}
                </p>
              </div>
              <div className="md:justify-self-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A6C70]">Actuals Data Range</p>
                <p className="mt-1 font-semibold text-[#1F1A20]">
                  {actualsRangeLabel}
                </p>
              </div>
              <div className="md:justify-self-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A6C70]">Fiscal Period Range</p>
                <p className="mt-1 font-semibold text-[#1F1A20]">
                  {periodRangeLabel}
                </p>
              </div>
            {showReportViewToggle ? (
              <div className="inline-flex h-10 w-auto rounded-md border border-[#D9D0C8] bg-white p-1 shadow-sm md:justify-self-end">
                <button
                  type="button"
                  onClick={() => setView('dashboard')}
                  className={`inline-flex h-8 flex-1 items-center justify-center gap-2 rounded px-3 text-sm font-semibold sm:flex-none ${view === 'dashboard' ? 'bg-[#6E3450] text-white' : 'text-[#4D4649] hover:bg-[#F5EFE8]'}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  className={`inline-flex h-8 flex-1 items-center justify-center gap-2 rounded px-3 text-sm font-semibold sm:flex-none ${view === 'grid' ? 'bg-[#6E3450] text-white' : 'text-[#4D4649] hover:bg-[#F5EFE8]'}`}
                >
                  <Table2 className="h-4 w-4" />
                  Daily Grid
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

	        {(loadingLastReport || refreshing || statusError || refreshCompletedAt) ? (
	          <section
	            ref={statusError ? errorRef : undefined}
	            role={statusError ? 'alert' : undefined}
	            className={`flex items-start gap-3 rounded-md px-4 py-3 text-sm ${
	              statusError
	                ? 'bg-[#FFF7F7] text-[#8B1D1D]'
	                : 'bg-white/70 text-[#4D4649]'
	            }`}
	          >
	            {statusError ? (
	              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
	            ) : refreshCompletedAt && !loadingLastReport && !refreshing ? (
	              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6F4E]" />
	            ) : (
	              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#6E3450]" />
	            )}
	            <div>
	              <p className="font-semibold text-[#1F1A20]">
	                {statusTitle}
	              </p>
	              <p className="mt-0.5 whitespace-pre-wrap break-words">{statusMessage}</p>
	            </div>
	          </section>
	        ) : null}

        {calculationReport?.labor_p3_report_validation ? (
          <section className="flex gap-3 rounded-md border border-[#E8C17D] bg-[#FFF7E8] px-4 py-3 text-sm text-[#6F4A12]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">Workbook validation enabled</p>
              <p className="mt-0.5">
                This view is showing calculated LABOR {reportPeriodLabel} results; workbook cached-value validation is available only as an explicit debug option.
              </p>
            </div>
          </section>
        ) : null}

        {!loading && !reportControlsDisabled && hasRenderableContent && dataQualityMessages.length > 0 ? (
          <section className="flex gap-3 rounded-md border border-[#E8C17D] bg-[#FFF7E8] px-4 py-3 text-sm text-[#6F4A12]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-2">
              <p className="font-semibold text-[#1F1A20]">Data quality notes</p>
              <ul className="space-y-2">
                {dataQualityMessages.map((message) => (
                <li key={message.id} className={dataQualityMessages.length > 1 ? 'flex gap-2.5' : undefined}>
                  {dataQualityMessages.length > 1 ? (
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A76A1D]" aria-hidden="true" />
                  ) : null}
                  <div>
                    <p className="font-semibold">{message.title}</p>
                    <p className="mt-0.5">{message.body}</p>
                  </div>
                </li>
                ))}
              </ul>
            </div>
          </section>
        ) : null}

        {loading && loadingPlants ? (
          <section className="flex min-h-[360px] items-center justify-center rounded-md border border-[#E6DED6] bg-white">
            <div className="flex items-center gap-3 text-sm text-[#5E5256]">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading plant list...
            </div>
          </section>
        ) : error ? (
          null
        ) : !hasRenderableContent && !loading ? (
          refreshing || statusError ? null : (
            <section className="rounded-md border border-[#E6DED6] bg-white px-5 py-6 text-sm text-[#5E5256]">
              <p className="font-semibold text-[#1F1A20]">
                No report data available
              </p>
              <p className="mt-1">
                Run the Labor Performance ingestion first, then refresh this report.
              </p>
            </section>
          )
        ) : (
          <div className="flex flex-col gap-5">
            {view === 'dashboard' ? (
              <>
                <PeriodStorySection
                  netVariance={netLaborVariance}
                  netVarianceMeta={netVarianceMeta}
                  narrative={periodStoryNarrative}
                  loading={periodStoryLoading}
                  reportLoading={loadingLastReport}
                  error={periodStoryError}
                />
                <LaborKpiSection metrics={kpiMetrics} fiscalYearPeriod={reportPeriod} loading={loadingLastReport} />
                <LaborPerformanceDeepDive
                  comparableFixedBudget={comparableFixedBudget}
                  comparableVariableEarned={comparableVariableEarned}
                  comparableFixedSpend={comparableFixedSpend}
                  comparableVariableSpend={comparableVariableSpend}
                  netVariance={netLaborVariance}
                  producedUnits={producedUnits}
                  netVarianceMeta={netVarianceMeta}
                  departmentRows={fixedDepartmentRows}
                  lineRows={lineChartRows}
                  exceptionRows={dailyExceptionRows}
                  loading={loadingLastReport}
                />
                {!loadingLastReport ? (
                  <>
                    <LaborChartsSection dailyRows={dailyChartRows} lineRows={lineChartRows} />
                    <LaborLineTotals report={calculationReport} />
                  </>
                ) : null}
              </>
            ) : (
              <>
                {!loadingLastReport ? (
                  <SectionPicker<LaborGridSection>
                    label="Daily Grid Sections"
                    options={gridSectionOptions}
                    value={gridSection}
                    onChange={setGridSection}
                    action={(
                      <button
                        type="button"
                        onClick={() => void handleDownloadGrid()}
                        disabled={!canDownloadGrid || downloadingGrid}
                        className="inline-flex h-9 items-center gap-2 rounded-md border border-[#D9D0C8] bg-white px-3 text-sm font-medium text-[#2F2A2D] shadow-sm transition hover:bg-[#F5EFE8] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {downloadingGrid ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        {downloadingGrid ? 'Preparing...' : 'Download'}
                      </button>
                    )}
                  />
                ) : null}
                {!loadingLastReport && showFixedGrid ? (
                  <>
                    <LaborDetailGrid title="Fixed Labor Daily Grid" section="fixed" contentWidth={contentWidth} data={detailViews.fixed} showFooter={false} />
                    <FixedAllocationTable report={calculationReport} />
                    <LaborFlatDailyTable
                      title="Fixed Debit / Production Support"
                      badge="Fixed"
                      rows={fixedRows}
                      columns={fixedSupportColumns}
                      minWidth={920}
                    />
                  </>
                ) : null}
                {!loadingLastReport && showVariableGrid ? (
                  <>
                    <LaborGroupedDailyTable
                      title="Variable Labor Daily Grid"
                      badge="Variable"
                      rows={variableRows}
                      groups={variableSummaryGroups}
                      minWidth={2200}
                    />
                    <LaborFlatDailyTable
                      title="Variable Credits / Debits"
                      badge="Variable"
                      rows={variableRows}
                      columns={variableAdjustmentColumns}
                      minWidth={980}
                    />
                    <MultiSectionPicker
                      label="Pack Line Sections"
                      options={variableGridSectionOptions}
                      value={variableGridDetailSections}
                      onChange={setVariableGridDetailSections}
                    />
                    <LaborDetailGrid
                      title="Pack Line Detail Tables"
                      section="variable"
                      contentWidth={contentWidth}
                      data={filteredVariableDetailView}
                      showTotals={false}
                    />
                  </>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
      {feedbackOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-6">
          <div className="w-full max-w-lg rounded-md border border-[#D9D0C8] bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 border-b border-[#E6DED6] px-5 py-4">
              <div>
                <p className="text-base font-semibold text-[#1F1A20]">Labor Performance Feedback</p>
                <p className="mt-1 text-sm text-[#6B5E62]">
                  {selectedPlantLabel} - {fiscalPeriod} - {view === 'dashboard' ? 'Dashboard' : 'Daily Grid'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFeedbackOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B5E62] transition hover:bg-[#F5EFE8] hover:text-[#1F1A20]"
                aria-label="Close feedback"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#4D4649]">Was this report helpful?</p>
                <div className="grid grid-cols-2 gap-2">
                  {feedbackSentimentOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = feedbackSentiment === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setFeedbackSentiment(option.id)}
                        className={`inline-flex h-10 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold transition ${
                          isSelected
                            ? 'border-[#6E3450] bg-[#6E3450] text-white shadow-sm'
                            : 'border-[#D9D0C8] bg-white text-[#4D4649] hover:bg-[#F5EFE8]'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex flex-col gap-1 text-sm font-medium text-[#4D4649]">
                What is this about?
                <select
                  value={feedbackCategory}
                  onChange={(event) => setFeedbackCategory(event.target.value)}
                  className="h-10 rounded-md border border-[#D9D0C8] bg-white px-3 text-sm font-semibold text-[#2F2A2D] outline-none transition focus:border-[#6E3450]"
                >
                  {feedbackCategoryOptions.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium text-[#4D4649]">
                Comments
                <textarea
                  value={feedbackComment}
                  onChange={(event) => {
                    setFeedbackComment(event.target.value);
                    setFeedbackError(null);
                  }}
                  rows={5}
                  maxLength={4000}
                  placeholder="Describe what looks wrong, what you expected, or what would help."
                  className="min-h-32 resize-y rounded-md border border-[#D9D0C8] bg-white px-3 py-2 text-sm text-[#2F2A2D] outline-none transition placeholder:text-[#9A8E86] focus:border-[#6E3450]"
                />
              </label>
              <div className="rounded-md bg-[#FAF7F1] px-3 py-2 text-xs leading-5 text-[#6B5E62]">
                SmartCHG will save this with the selected plant, fiscal period, current view, and basic report status.
              </div>
              {feedbackError ? <p className="text-sm font-semibold text-[#B3261E]">{feedbackError}</p> : null}
            </div>
            <div className="flex justify-end gap-2 border-t border-[#E6DED6] px-5 py-4">
              <button
                type="button"
                onClick={() => setFeedbackOpen(false)}
                disabled={feedbackSubmitting}
                className="inline-flex h-10 items-center justify-center rounded-md border border-[#D9D0C8] bg-white px-4 text-sm font-semibold text-[#4D4649] transition hover:bg-[#F5EFE8] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSubmitFeedback()}
                disabled={feedbackSubmitting}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#6E3450] px-4 text-sm font-semibold text-white transition hover:bg-[#5A2A41] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {feedbackSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
